import PromptHeader from '../components/PromptHeader.jsx';
import {useEffect, useState, useMemo, startTransition, useRef} from "react";
import {db} from "../db";
import { databaseOperations } from '../services/DatabaseQueue';
import { isFeatureEnabled } from '../config/featureFlags';
import { getMediaType } from '../utils/MediaOperations';
import jobManager from '../services/JobManager';
import {
    isWorkflowSwitchRequired,
    clearWorkflowSwitchFlag,
    getOriginalWorkflow,
    workflowAcceptsImages,
    saveLastUsedImageWorkflow
} from '../utils/workflowUtils';
import { 
    getDefaultImageEditingWorkflow, 
    getBestImageEditingWorkflow 
} from '../config/defaultWorkflows';
import CollectionImage from "../components/CollectionImage";
import {useGenerateDispatch, useGenerateStatus} from "../context/GenerateContext";
import {accountNosignin, cancelJob, generateOutput, generatePrompt, getCollectionHashes, getProgress, updateAccount, getGenerateOutput, setReceived, getUndeliveredImages, getTokens, getComfyOutput, cancelCosyJob, getApiBaseUrl} from "../api-helper";
import { getServerForModel } from '../services/offlineModelService';
import { recoverSingleJob, savePendingJob, clearPendingJob, clearAllPendingJobs } from '../services/jobRecoveryService';
import FinishSound from '../sounds/finished.flac';
import md5 from "blueimp-md5"
import DrawingInput from "../components/inputs/DrawingInput";
import CloseIcon from "../images/icons/white-close.svg";
import DoubleRightIcon from "../images/icons/white-double-right.svg";
import DoubleLeftIcon from "../images/icons/white-double-left.svg";
import GreyDoubleRightIcon from "../images/icons/grey-double-right.svg";
import GreyDoubleLeftIcon from "../images/icons/grey-double-left.svg";
import Logo from "../images/logo_light_grey.png";
import PromptSpin from "../images/prompts.lottie";
import * as LottiePlayer from "@lottiefiles/lottie-player";
import {usePromptusAPIState} from "../context/PromptusAPIContext";
import { useAccountContext, useAccountContextDispatch } from '../context/AccountContext.js';
import Swal from 'sweetalert2';
import { useCollectionContext, useCollectionContextDispatch } from '../context/CollectionContext.js';
import { useDriverContext, useDriverContextDispatch } from '../context/DriverContext.js';
import { isUndefined } from 'cropperjs';
import { InView } from 'react-intersection-observer';
import LazyLoad from 'react-lazyload';
import { fileTypeFromBuffer } from 'file-type';
import Masonry from 'react-masonry-css';
import Video from '../components/Video.jsx';
import Audio from '../components/Audio.jsx';
import electronBridge from '../utils/electronBridge';
import eventBus from '../utils/eventBus';
import { SiDiscord } from 'react-icons/si';
import FullImageViewer from "../components/FullImageViewer";
import { getActiveOfflineMode } from '../utils/offlineModeHelper.js';
import PromptHeaderExperiment from '../components/PromptHeaderExperiment.jsx';
import { useUndeliveredImages } from '../hooks';

// Lazy getter for JobManager to avoid circular dependency issues
const getJobManager = () => jobManager;

export const defaultPrompt = {
    accountNo: "",
    error: "",
    progress: 0,
    prompt: {
        prompt: "",
        negative_prompt: "",
        enhanced: false,
        prompt_output: "text-image",
        audio_prompt: "",
        input_language: "en",
        meme_prompt: false,
        params: {
            seed_value: null,
            num_inference_steps: 12,
            guidance_scale: 7.5,
            scheduler: "DPM-Solver",
            model: "(AI-Horde) Flux.1-Schnell fp8 (Compact)",
            lora: "None",
            lora_scale: 0,
            width: 512,
            height: 512,
            count: 1,
            symmetric: false,
            shuffle: false,
            safety_checker: true,
            input_image: null,
            upscale: false,
            upscale_factor: 0,
            control_net: "",
            mask_image: null,
            image_batch_size: 1
        }
    }
};

const validateMediaUrl = async (url) => {
    if (!url || typeof url !== 'string') {
        return false;
    }

    try {
        const response = await fetch(url, {
            method: 'HEAD',
            headers: {
                'Accept': 'image/*, video/*, audio/*'
            },
            timeout: 5000
        });

        if (!response.ok) {
            return false;
        }

        const contentType = response.headers.get('content-type');
        if (!contentType) {
            return false;
        }

        const isValidMediaType = contentType.startsWith('image/') ||
                                contentType.startsWith('video/') ||
                                contentType.startsWith('audio/');

        return isValidMediaType;
    } catch (error) {
        console.warn(`[URL_VALIDATION] Failed to validate URL ${url}:`, error.message);
        return false;
    }
};


function Placeholder(props){
    const generateStatus = useGenerateStatus();
    if (props.Count >= 1) {
        const renderPlaceholders = () => {
            const placeholders = [];
            
            for (let i = 0; i < props.Count; i++) {
                placeholders.push(
                    <div key={`placeholder-${i}`} className={"group w-full rounded-xl overflow-hidden relative bg-gray-400 animate-pulse aspect-square"}>
                        <img className={"w-full h-full object-contain"} src="https://i.postimg.cc/632gRsSp/Final-logo-Black.png" alt='Help'/>
                    </div>
                );
            }
            
            return placeholders;
        };  
        return renderPlaceholders()
    }
    return null;
}
  

export default function Generate() {
    const generateStatusDispatch = useGenerateDispatch();
    const generateStatus = useGenerateStatus();
    const accountContextDispatch = useAccountContextDispatch();
    const accountContext = useAccountContext();
    const apiState = usePromptusAPIState();
    const driverContextDispatch = useDriverContextDispatch();
    const { checkingUndeliveredImages } = useUndeliveredImages();
    const [imageList, setImageList] = useState([]);
    const [columnClass, setColumnClass] = useState("cols-4");
    const [zoomedImage, setZoomedImage] = useState(null);
    const [loadingImages, setLoadingImages] = useState(false);
    const [isGalleryInitializing, setIsGalleryInitializing] = useState(true);
    const [animations, setAnimations] = useState(false);
    const [localDirectory, setLocalDirectory] = useState("");
    const [redoPromptUid, setRedoPromptUid] = useState("");
    const [redoPrompt, setRedoPrompt] = useState("");
    const [redoSeed, setRedoSeed] = useState("");
    const [inputImage, setInputImage] = useState({});
    const [category, setCategory] = useState("All");
    const [categories, setCategories] = useState([]);
    const [categories2, setCategories2] = useState([]);
    const collectionContextDispatch = useCollectionContextDispatch();
    const collectionContext = useCollectionContext();
    const [showAll, setShowAll] = useState(false);
    const prevShowAllRef = useRef(showAll);
    const [empty, setEmpty] = useState(false);
    const [page, setPage] = useState(0);
    const [totalStoredImages, setTotalStoredImages] = useState(0);
    const [workflowSwitchNotification, setWorkflowSwitchNotification] = useState(null);
    const [originalWorkflowBeforeSwitch, setOriginalWorkflowBeforeSwitch] = useState(null);
    const [onlineImages, setOnlineImages] = useState([]);
    const [account, setAccount] = useState({});
    const [sortMethod, setSortMethod] = useState("Newest");
    const [failedImage, setFailedImage] = useState(false);
    const [failedImageID, setFailedImageID] = useState("");
    const [viewFailedImage, setViewFailedImage] = useState(false);
    const [failedImageDetails, setFailedImageDetails] = useState({});
    const [isFetching, setIsFetching] = useState(false);
    const [sameSeed, setSameSeed] = useState(false);
    const [fetchingUndelivered, setFetchingUndelivered] = useState(false);
    const [sameSeedDate, setSameSeedDate] = useState(0);
    const [queryBusy, setQueryBusy] = useState(false);
    const [deepLinkModel, setDeepLinkModel] = useState(null);
    const [templateData, setTemplateData] = useState(null);
    // const [processedUIDS, setProcessedUIDS] = useState([]);
    const [jobTimeouts, setJobTimeouts] = useState({}); // Track timeout states for each job

    const [viewerOpen, setViewerOpen] = useState(false);
    const [viewerIndex, setViewerIndex] = useState(0);

    const [modelUnavTest, setModelUnavTest] = useState(false)

    // Performance constants and refs
    const REFRESH_DEBOUNCE_MS = 300; // 300ms minimum between calls
    const QUERY_TIMEOUT_MS = 15000; // 15 seconds max query time
    const REQUEST_DEDUPE_MS = 1000; // 1 second deduplication window

    const refreshSeqRef = useRef(0);
    // GLOBAL DEBOUNCE for refreshImages to prevent rapid successive calls
    const lastRefreshTime = useRef(0);

    // STRONGER CONCURRENCY CONTROL: Global debounce timer and call tracking
    const refreshDebounceTimer = useRef(null);
    const pendingRefreshArgs = useRef(null);
    const isRefreshScheduled = useRef(false);

    // ENHANCED GLOBAL LOCK: Timeout handling and sequence tracking
    const queryTimeout = useRef(null);
    const activeCallId = useRef(null);

    // REQUEST DEDUPLICATION: Track recent requests to prevent duplicates
    const recentRequests = useRef(new Map()); // key -> { timestamp, callId }
    const refreshInProgressRef = useRef(false);
    const ipcRenderer = electronBridge.ipcRenderer

    // Only images we can actually show (skip placeholders/failed)
    const viewable = useMemo(
        () => imageList.filter(i => i?.image && i.image !== "" && i.dataType !== 'video' && i.dataType !== 'audio' && i.dataType !== 'placeholder'),
        [imageList]
    );

    const openViewerAt = (byHashOrIndex) => {
      const idx = typeof byHashOrIndex === "number"
        ? byHashOrIndex
        : viewable.findIndex(i => i.hash === byHashOrIndex);
      if (idx >= 0) {
        setViewerIndex(idx);
        setViewerOpen(true);
        document.body.style.overflow = "hidden"; // lock scroll
      }
    };

    const closeViewer = () => {
      setViewerOpen(false);
      document.body.style.overflow = "";
    };  

    const nextImage = () => {
      setViewerIndex((i) => (i + 1) % viewable.length);
    };
    
    const prevImage = () => {
      setViewerIndex((i) => (i - 1 + viewable.length) % viewable.length);
    };

    useEffect(() => {
        const handler = () => refreshImages(true); // or whatever args you want
        eventBus.on('refreshImages', handler);
        return () => eventBus.off('refreshImages', handler);
      }, []);

    useEffect(() => {
      const handler = () => {
        console.log('[EVENT] Received run_placeholderCleanup event from NavigationMenu polling');
        cleanupRemainingPlaceholders();
      };
      eventBus.on('run_placeholderCleanup', handler);
      return () => eventBus.off('run_placeholderCleanup', handler);
    }, []);

    // Listen for job manager poll events
    useEffect(() => {
      const handler = (uid) => {
        console.log(`[JOB_MANAGER] Received poll_job event for ${uid}`);
        fetchJob(uid);
      };
      eventBus.on('poll_job', handler);
      return () => eventBus.off('poll_job', handler);
    }, []);

    // Check animations setting
    useEffect(() => {
        const checkAnimationsEnabled = async () => {
            try {
                const setting = await db.settings.where("key").equals("enable_animations").first();
                setAnimations(setting ? setting.value : false);
                if (!setting) {
                    await db.settings.put({ key: 'enable_animations', value: false });
                }
            } catch (error) {
                console.error("Error checking enable_animations setting:", error);
                setAnimations(false);
            }
        };
        checkAnimationsEnabled();
    }, []);

    // Use electronBridge for logging to avoid browser errors
    electronBridge.log("React - Generate Page");

    // Handle deep link model
    useEffect(() => {
        // Check for deep link model on component mount
        const storedModel = sessionStorage.getItem('deepLinkModel');
        if (storedModel) {
            console.log('Deep link model found:', storedModel);
            setDeepLinkModel(storedModel);
            // Clear it after reading to prevent it from persisting
            sessionStorage.removeItem('deepLinkModel');

            // Clear the deep link model after a short delay to allow PromptHeader to process it
            setTimeout(() => {
                setDeepLinkModel(null);
            }, 1000);
        }

        // Check for template data from Templates page
        const storedTemplate = sessionStorage.getItem('selectedTemplate');
        if (storedTemplate) {
            try {
                const templateObj = JSON.parse(storedTemplate);
                console.log('Template data found:', templateObj);
                setTemplateData(templateObj);
                // Clear it after reading to prevent it from persisting
                sessionStorage.removeItem('selectedTemplate');

                // Clear the template data after a short delay to allow PromptHeader to process it
                setTimeout(() => {
                    setTemplateData(null);
                }, 1500);
            } catch (error) {
                console.error('Error parsing template data:', error);
                sessionStorage.removeItem('selectedTemplate');
            }
        }

        // Listen for model deep link events
        const handleModelDeepLink = (event) => {
            console.log('Model deep link event received:', event.detail);
            if (event.detail && event.detail.modelName) {
                setDeepLinkModel(event.detail.modelName);
                
                // Clear the deep link model after a short delay
                setTimeout(() => {
                    setDeepLinkModel(null);
                }, 1000);
            }
        };

        window.addEventListener('modelDeepLink', handleModelDeepLink);

        return () => {
            window.removeEventListener('modelDeepLink', handleModelDeepLink);
        };
    }, []);

    // Add useEffect to check for a selected image in localStorage
    useEffect(() => {
        // Check if an image was passed from ComfyOutput page
        const selectedImage = localStorage.getItem('selected_image');
        const selectedImageHash = localStorage.getItem('selected_image_hash');
        
        if (selectedImage && selectedImageHash) {
            console.log("Found image from ComfyOutput page:", selectedImageHash);
            
            // Emit useImage event to PromptHeader
            eventBus.emit('useImage', {
                image: selectedImage,
                uid: selectedImageHash
            });
            
            // Activate the image input option
            driverContextDispatch({type: 'controlNet'});
            
            // Clear localStorage items
            localStorage.removeItem('selected_image');
            localStorage.removeItem('selected_image_hash');
        }

        (async () => {
            const jobIds = Object.keys(generateStatus.jobs || {});
            console.log('[GENERATE STARTUP] generateStatus: ', generateStatus, ` with the jobs length being: ${jobIds.length}`);
            
            if (!jobIds.length) {
                console.log('RUNNING PLACEHOLDER CLEANUP FOR ANY ACTIVE PLACEHOLDERS');
                // eventBus.emit('run_placeholderCleanup');
                await cleanupRemainingPlaceholders()
            }
        })();
    }, []);

    // Trigger undelivered images check when entering Generate page
    useEffect(() => {
        console.log("[GENERATE] Component mounted, triggering undelivered images check");
        checkingUndeliveredImages();
    }, [checkingUndeliveredImages]);

    useEffect(() => {
        // Check for temporary image data when component mounts
        const transferActive = localStorage.getItem('image_transfer_active');
        
        // Check if we're in a page transition
        const inPageTransition = localStorage.getItem('page_transition') === 'true';
        const navigationTimestamp = localStorage.getItem('navigation_timestamp');
        const isRecentNavigation = navigationTimestamp && 
            (Date.now() - parseInt(navigationTimestamp)) < 30000; // Within last 30 seconds
        
        // Log diagnostics for debugging
        console.log("🔍 Generate.jsx - Image Transfer Diagnostics:", {
            transferActive,
            inPageTransition,
            navigationTimestamp,
            isRecentNavigation,
            temp_image_data_exists: !!localStorage.getItem('temp_image_data'),
            temp_image_data_length: localStorage.getItem('temp_image_data')?.length || 0,
            selected_image_exists: !!localStorage.getItem('selected_image'),
            selected_image_length: localStorage.getItem('selected_image')?.length || 0,
            transfer_image_object_exists: !!localStorage.getItem('transfer_image_object'),
            debug_transfer_exists: !!localStorage.getItem('debug_image_transfer'),
            emergency_fallback_exists: !!localStorage.getItem('emergency_fallback_image')
        });
        
        // If we're in a page transition, make sure to clear the flag
        if (inPageTransition) {
            console.log("✅ Detected page transition from MOMM to Generate");
            localStorage.removeItem('page_transition');
        }
        
        if (transferActive === 'true' || (inPageTransition && isRecentNavigation)) {
            console.log("✅ Generate component found active image transfer");
            
            // Try different image sources in order of preference
            let imageData = localStorage.getItem('temp_image_data') || 
                            localStorage.getItem('image_data_url') || 
                            localStorage.getItem('emergency_fallback_image');
            
            if (!imageData && localStorage.getItem('selected_image')) {
                // If we only have selected_image, format it correctly
                const rawBase64 = localStorage.getItem('selected_image');
                if (rawBase64) {
                    console.log("🔄 Converting selected_image to data URL format");
                    imageData = rawBase64.startsWith('data:') ? 
                        rawBase64 : 
                        `data:image/png;base64,${rawBase64}`;
                }
            }
            
            if (imageData) {
                console.log("✅ Found image data, length:", imageData.length, 
                    "format:", imageData.startsWith('data:') ? 'Data URL' : 
                    imageData.startsWith('{') ? 'JSON Object' : 'Unknown');
                
                // Set input image directly with better error handling
                try {
                    // Set the input image directly
                    setInputImage({image: imageData});
                    console.log("✅ Set input image directly");
                    
                    // Update contexts for immediate use
                    collectionContextDispatch({type: 'image', image: imageData});
                    driverContextDispatch({type: 'controlNet'});
                    console.log("✅ Updated contexts with image data");
                } catch (e) {
                    console.error("❌ Error setting input image:", e);
                    
                    // Try alternate approach if direct setting fails
                    try {
                        // Parse as JSON in case it's structured
                        const parsedData = JSON.parse(imageData);
                        if (parsedData.image) {
                            setInputImage({image: parsedData.image});
                            collectionContextDispatch({type: 'image', image: parsedData.image});
                            driverContextDispatch({type: 'controlNet'});
                            console.log("✅ Used JSON image data as fallback");
                        }
                    } catch (jsonError) {
                        console.error("❌ JSON parsing failed:", jsonError);
                    }
                }
                
                // Add a visual indicator that the image was received
                const indicator = document.createElement('div');
                indicator.style.position = 'fixed';
                indicator.style.top = '10px';
                indicator.style.left = '10px';
                indicator.style.backgroundColor = 'rgba(25, 10, 78, 0.8)';
                indicator.style.color = 'white';
                indicator.style.padding = '8px';
                indicator.style.borderRadius = '4px';
                indicator.style.zIndex = '9999';
                indicator.style.transition = 'opacity 0.5s';
                indicator.id = 'image-transfer-indicator';
                indicator.textContent = 'Image transferred successfully';
                document.body.appendChild(indicator);
                
                // Clear all transfer flags after a delay
                setTimeout(() => {
                    // Clear all localStorage keys related to image transfer
                    const keysToRemove = [
                        'image_transfer_active',
                        'temp_image_data',
                        'temp_image_object',
                        'image_transfer_timestamp',
                        'selected_image',
                        'selected_image_hash',
                        'selected_image_prompt',
                        'image_base64_raw',
                        'image_data_url',
                        'emergency_fallback_image',
                        'navigation_timestamp',
                        'transfer_image_object'
                    ];
                    
                    for (const key of keysToRemove) {
                        localStorage.removeItem(key);
                    }
                    
                    console.log("✅ Cleared all image transfer flags");
                    
                    // Fade out and remove the indicator
                    setTimeout(() => {
                        const ind = document.getElementById('image-transfer-indicator');
                        if (ind) {
                            ind.style.opacity = '0';
                            setTimeout(() => {
                                if (ind && ind.parentNode) {
                                    ind.parentNode.removeChild(ind);
                                }
                            }, 500);
                        }
                    }, 3000);
                }, 2000); // Longer delay to ensure all processing completes
            } else {
                console.warn("❌ Transfer active but no image data found in localStorage");
            }
        } else {
            // Check alternate methods - this ensures compatibility with various components
            console.log("📌 No active transfer flag, checking alternate methods");
            
            // Check if an image was passed from ComfyOutput page
            const selectedImage = localStorage.getItem('selected_image');
            const selectedImageHash = localStorage.getItem('selected_image_hash');
            
            if (selectedImage && selectedImageHash) {
                console.log("✅ Found image from alternative storage method (selected_image)");
                
                // Format check
                let formattedImage = selectedImage;
                if (typeof selectedImage === 'string' && !selectedImage.startsWith('data:')) {
                    console.log("📌 Adding data URL prefix to selected_image");
                    formattedImage = `data:image/png;base64,${selectedImage}`;
                }
                
                // Set the input image
                setInputImage({
                    image: formattedImage,
                    hash: selectedImageHash
                });
                
                // Activate the image input option
                collectionContextDispatch({type: 'image', image: formattedImage});
                driverContextDispatch({type: 'controlNet'});
                console.log("✅ Updated contexts with selected_image data");
                
                // Clear localStorage items
                localStorage.removeItem('selected_image');
                localStorage.removeItem('selected_image_hash');
                localStorage.removeItem('selected_image_prompt');
            }
            
            // Check for transfer_image_object format
            const transferImageObject = localStorage.getItem('transfer_image_object');
            if (transferImageObject) {
                try {
                    const parsedObject = JSON.parse(transferImageObject);
                    console.log("✅ Found and parsed transfer_image_object");
                    
                    if (parsedObject && parsedObject.image) {
                        // Set the input image
                        setInputImage({
                            image: parsedObject.image,
                            hash: parsedObject.hash || '',
                            uid: parsedObject.uid || ''
                        });
                        
                        // Activate the image input option
                        collectionContextDispatch({type: 'image', image: parsedObject.image});
                        driverContextDispatch({type: 'controlNet'});
                        console.log("✅ Updated contexts with transfer_image_object data");
                    }
                    
                    // Clear this localStorage item
                    localStorage.removeItem('transfer_image_object');
                } catch (e) {
                    console.error("❌ Error parsing transfer_image_object:", e);
                }
            }
            
            // Check for debug data
            const debugTransfer = localStorage.getItem('debug_image_transfer');
            if (debugTransfer) {
                console.log("📌 Debug transfer data:", debugTransfer);
                // Keep for debugging
            }
            
            // Check additional backup methods
            const rawBase64 = localStorage.getItem('image_base64_raw');
            const dataUrl = localStorage.getItem('image_data_url');
            
            if (dataUrl || rawBase64) {
                console.log("✅ Found image from backup transfer methods");
                
                // Prefer data URL format if available
                if (dataUrl) {
                    console.log("📌 Using image_data_url backup method");
                    setInputImage({image: dataUrl});
                    collectionContextDispatch({type: 'image', image: dataUrl});
                    driverContextDispatch({type: 'controlNet'});
                } else if (rawBase64) {
                    console.log("📌 Using image_base64_raw backup method");
                    const formattedData = `data:image/png;base64,${rawBase64}`;
                    setInputImage({image: formattedData});
                    collectionContextDispatch({type: 'image', image: formattedData});
                    driverContextDispatch({type: 'controlNet'});
                }
                
                // Show visual indicator
                const indicator = document.createElement('div');
                indicator.style.position = 'fixed';
                indicator.style.top = '10px';
                indicator.style.left = '10px';
                indicator.style.backgroundColor = 'rgba(25, 10, 78, 0.8)';
                indicator.style.color = 'white';
                indicator.style.padding = '8px';
                indicator.style.borderRadius = '4px';
                indicator.style.zIndex = '9999';
                indicator.textContent = 'Image loaded from backup method';
                document.body.appendChild(indicator);
                
                // Clear after 3 seconds
                setTimeout(() => {
                    if (indicator.parentNode) {
                        indicator.parentNode.removeChild(indicator);
                    }
                    
                    // Clear localStorage items
                    localStorage.removeItem('image_base64_raw');
                    localStorage.removeItem('image_data_url');
                }, 3000);
            }
            
            // CRITICAL FIX: Check for emergency fallback image
            const emergencyImage = localStorage.getItem('emergency_fallback_image');
            if (emergencyImage) {
                console.log("⚠️ Using emergency fallback image");
                try {
                    // Use the emergency image
                    setInputImage({image: emergencyImage});
                    collectionContextDispatch({type: 'image', image: emergencyImage});
                    driverContextDispatch({type: 'controlNet'});
                    
                    // Show recovery indicator
                    const recoveryIndicator = document.createElement('div');
                    recoveryIndicator.style.position = 'fixed';
                    recoveryIndicator.style.top = '10px';
                    recoveryIndicator.style.right = '10px';
                    recoveryIndicator.style.backgroundColor = 'rgba(220, 53, 69, 0.8)';
                    recoveryIndicator.style.color = 'white';
                    recoveryIndicator.style.padding = '8px';
                    recoveryIndicator.style.borderRadius = '4px';
                    recoveryIndicator.style.zIndex = '9999';
                    recoveryIndicator.textContent = 'Image transfer recovery mode';
                    document.body.appendChild(recoveryIndicator);
                    
                    // Clear after 5 seconds
                    setTimeout(() => {
                        if (recoveryIndicator.parentNode) {
                            recoveryIndicator.parentNode.removeChild(recoveryIndicator);
                        }
                        localStorage.removeItem('emergency_fallback_image');
                    }, 5000);
                } catch (error) {
                    console.error("❌ Error using emergency fallback image:", error);
                }
            }
        }
    }, [collectionContextDispatch, driverContextDispatch]);

    // Check for workflow switching when image is transferred
    useEffect(() => {
        // Only check if we have an input image and workflow switch is required
        if (inputImage && inputImage.image && isWorkflowSwitchRequired()) {
            console.log("🔄 Checking workflow compatibility for transferred image");
            
            // Get current workflow/model information
            const currentModel = generateStatus?.promptData?.prompt?.model || 
                                localStorage.getItem('current_model') || 
                                defaultPrompt.prompt.params.model;
            
            // Check if current workflow accepts images
            // For now, we'll check if the model name suggests image capability
            // This can be enhanced with actual schema checking when available
            const modelName = currentModel?.toLowerCase() || '';
            const acceptsImages = modelName.includes('img2img') || 
                                 modelName.includes('image') ||
                                 modelName.includes('inpaint') ||
                                 modelName.includes('controlnet') ||
                                 modelName.includes('pix2pix') ||
                                 modelName.includes('edit');
            
            if (!acceptsImages) {
                console.log(`⚠️ Current model "${currentModel}" doesn't accept images. Switching to image-compatible workflow.`);
                
                // Store original workflow for potential revert
                const originalWorkflow = getOriginalWorkflow();
                setOriginalWorkflowBeforeSwitch(originalWorkflow || { model: currentModel });
                
                // Get best image editing workflow
                const imageWorkflow = getBestImageEditingWorkflow(currentModel);
                
                if (imageWorkflow) {
                    // Update the model in generate context
                    // This will be handled by PromptHeader component
                    const newModel = imageWorkflow.model || imageWorkflow.id;
                    
                    // Set notification for user
                    setWorkflowSwitchNotification({
                        type: 'info',
                        message: `Switched to "${imageWorkflow.name}" for image editing`,
                        originalModel: currentModel,
                        newModel: newModel,
                        canRevert: true
                    });
                    
                    // Save as last used image workflow
                    saveLastUsedImageWorkflow(imageWorkflow);
                    
                    // Update localStorage for PromptHeader to pick up
                    localStorage.setItem('auto_selected_model', newModel);
                    localStorage.setItem('workflow_auto_switched', 'true');
                    
                    // Trigger model change event for PromptHeader
                    window.dispatchEvent(new CustomEvent('modelAutoSwitch', {
                        detail: {
                            newModel: newModel,
                            reason: 'image_compatibility'
                        }
                    }));
                    
                    // Clear the workflow switch flag
                    clearWorkflowSwitchFlag();
                    
                    // Auto-dismiss notification after 5 seconds
                    setTimeout(() => {
                        setWorkflowSwitchNotification(null);
                    }, 5000);
                }
            } else {
                console.log(`✅ Current model "${currentModel}" already accepts images`);
                // Clear the flag since no switch is needed
                clearWorkflowSwitchFlag();
            }
        }
    }, [inputImage, generateStatus]);

    function findKeyValues(data, targetKey) {
        const results = [];
      
        function recursiveSearch(item) {
          if (Array.isArray(item)) {
            for (const element of item) {
              recursiveSearch(element);
            }
          } else if (typeof item === 'object' && item !== null) {
            for (const [key, value] of Object.entries(item)) {
              if (key === targetKey) {
                results.push(value);
              }
              recursiveSearch(value);
            }
          }
        }
      
        recursiveSearch(data);
        return results;
      }
      
      const toFileUrl = (filePath) => {
        const fixed = filePath.replace(/\\/g, '/'); // \ -> /
        const encodedURL = `safe-file://${encodeURI(fixed)}`;
        return encodedURL;
      }


    /**
     * Check if a string is valid base64
     * @param {string} str - The string to check
     * @returns {boolean} - True if valid base64
     */
    const isValidBase64 = (str) => {
        if (!str || typeof str !== 'string') return false;
        try {
            // Remove data URL prefix if present
            const base64 = str.replace(/^data:.*?;base64,/, '');
            // Use regex to validate base64 instead of atob/btoa which can crash on large strings
            // This is much safer for large images in Electron
            const base64Regex = /^[A-Za-z0-9+/]*={0,2}$/;
            return base64Regex.test(base64) && base64.length % 4 === 0;
        } catch (err) {
            return false;
        }
    };

    /**
     * Convert image data to base64
     * @param {string|object} imageData - The image data (could be base64, data URL, or object with image property)
     * @returns {Promise<string>} - Base64 encoded string without data URL prefix
     */
    const convertToBase64 = async (imageData) => {
        if (!imageData) return null;
        
        // If it's an object with an image property, extract it
        if (typeof imageData === 'object' && imageData.image) {
            imageData = imageData.image;
        }
        
        // If it's already a plain base64 string (no data URL prefix)
        if (typeof imageData === 'string' && isValidBase64(imageData) && !imageData.startsWith('data:')) {
            return imageData;
        }
        
        // If it's a data URL, extract the base64 part
        if (typeof imageData === 'string' && imageData.startsWith('data:')) {
            const base64 = imageData.split(',')[1];
            if (base64 && isValidBase64(base64)) {
                return base64;
            }
        }
        
        // If it's a file path or blob URL, we might need to fetch and convert it
        // For now, return null as we'd need additional logic to handle these cases
        console.warn('[convertToBase64] Unable to convert image data:', typeof imageData);
        return null;
    };

    /**
     * Get nested object value using path array
     * @param {object} obj - The object to traverse
     * @param {string[]} path - Array of keys representing the path
     * @returns {*} - The value at the path or undefined
     */
    const getNestedValue = (obj, path) => {
        return path.reduce((current, key) => current?.[key], obj);
    };

    /**
     * Set nested object value using path array
     * @param {object} obj - The object to modify
     * @param {string[]} path - Array of keys representing the path
     * @param {*} value - The value to set
     */
    const setNestedValue = (obj, path, value) => {
        const pathCopy = [...path]; // Create a copy to avoid modifying the original
        const lastKey = pathCopy.pop();
        const target = pathCopy.reduce((current, key) => {
            if (!current[key]) current[key] = {};
            return current[key];
        }, obj);
        target[lastKey] = value;
    };

    /**
     * Get the user ID
     * @returns {Promise<*>}
     */
    const getUUID = async () => {
        return accountContext.accountDetails.accountNo
    }

    /**
     * Fetch cosyflow data from IndexedDB for a given model
     * @param {string} modelName - The name of the model to fetch data for
     * @returns {Promise<Object|null>} - The cosyflow variables or null if not found
     */
    const getCosyflowDataFromDB = async (modelName) => {
        try {
            console.log('[getCosyflowDataFromDB] Fetching data for model:', modelName);
            // Use indexed lookup for O(log n) performance instead of O(n)
            const matchingCosyflow = await db.cosyflows.where('data.title').equals(modelName).first();
            
            if (matchingCosyflow) {
                console.log('[getCosyflowDataFromDB] Found cosyflow data:', matchingCosyflow);
                return matchingCosyflow.data.variables || null;
            }
            
            console.log('[getCosyflowDataFromDB] No cosyflow data found for model:', modelName);
            return null;
        } catch (error) {
            console.error('[getCosyflowDataFromDB] Error fetching cosyflow data:', error);
            return null;
        }
    }

    /**
     * Generate a random 64-bit seed value
     * @returns {string} - A random seed value as a string
     */
    /* global BigInt */
    const generateRandomSeed = () => {
        // Generate a random 64-bit number using BigInt
        const randomBytes = new Uint8Array(8);
        crypto.getRandomValues(randomBytes);
        // Convert bytes to BigInt
        let randomBigInt = 0n;
        for (let i = 0; i < 8; i++) {
            randomBigInt = (randomBigInt << 8n) | BigInt(randomBytes[i]);
        }
        // Ensure positive and within 64-bit signed range (0 to 2^63-1)
        const maxInt64 = (1n << 63n) - 1n;
        const positiveBigInt = randomBigInt & maxInt64;
        return positiveBigInt.toString();
    };

    /**
     * Update the seed value in cosyflows table for a given model
     * @param {string} modelName - The name of the model to update
     * @param {string} newSeed - The new seed value to save
     * @returns {Promise<void>}
     */
    const updateCosyflowSeed = async (modelName, newSeed) => {
        try {
            console.log('[updateCosyflowSeed] Updating seed for model:', modelName, 'with new seed:', newSeed);
            // Use indexed lookup for O(log n) performance instead of O(n)
            const matchingCosyflow = await db.cosyflows.where('data.title').equals(modelName).first();
            
            if (matchingCosyflow) {
                // Create a deep copy of the data
                const updatedData = JSON.parse(JSON.stringify(matchingCosyflow.data));
                
                // Find and update all seed fields (only type 'seed')
                let seedFieldsUpdated = 0;
                for (const [key, variable] of Object.entries(updatedData.variables)) {
                    if (variable.type === 'seed') {
                        variable.default = newSeed;
                        console.log(`[updateCosyflowSeed] Updated ${key} to ${newSeed} (type: seed)`);
                        seedFieldsUpdated++;
                    }
                }
                
                // Update the record in IndexedDB using database queue if enabled
                if (isFeatureEnabled('USE_DATABASE_QUEUE')) {
                    // Note: We need to add a cosyflow update method to DatabaseOperations
                    // For now, use direct update since cosyflows is less critical for race conditions
                    await db.cosyflows.update(matchingCosyflow.id, {
                        data: updatedData,
                        timestamp: Date.now()
                    });
                } else {
                    await db.cosyflows.update(matchingCosyflow.id, {
                        data: updatedData,
                        timestamp: Date.now()
                    });
                }
                
                console.log(`[updateCosyflowSeed] Successfully updated ${seedFieldsUpdated} seed field(s) in cosyflows table`);
            } else {
                console.warn('[updateCosyflowSeed] No cosyflow found for model:', modelName);
            }
        } catch (error) {
            console.error('[updateCosyflowSeed] Error updating cosyflow seed:', error);
        }
    };


    // Create N - existingEmpty placeholders for this job, one-by-one (no delays)
const createPlaceholdersSequential = async (jobId, requestedCount) => {
    const count = Number(requestedCount || 1);
    if (!jobId || count <= 0) return;

    console.log(`[PLACEHOLDERS] Creating sequential placeholders for job ${jobId}, count: ${count}, queue enabled: ${isFeatureEnabled('USE_DATABASE_QUEUE')}`);

    // Only count EMPTY rows for this job
    const existingEmpty = await db.images
      .where('uid').equals(jobId)
      .and(i => !i.image || i.image === "")
      .count();

    const need = Math.max(0, count - existingEmpty);
    if (need === 0) return;

    const base = Date.now();

    for (let i = 0; i < need; i++) {
      const obj = {
        image: "",
        seed: "",
        uid: jobId,
        model: "",
        inCollection: false,
        hash: md5(String(Math.floor(Math.random() * 1e8))),
        online: apiState.online,
        tag: generateStatus.tag,
        // slight monotonic bump keeps "left→right" order stable if you render by time
        requestedtimestamp: base + i,
        receivedtimestamp: base + i,
        dataType: "placeholder",
      };

      // Use database queue if enabled, otherwise direct add
      let id;
      if (isFeatureEnabled('USE_DATABASE_QUEUE')) {
        id = await databaseOperations.addImage(obj);
      } else {
        id = await db.images.add(obj);
      }

      // Show immediately for "Newest"/"New_requested"
      if (sortMethod === "Newest" || sortMethod === "New_requested") {
        setImageList(prev => {
          const next = [{ ...obj, id }, ...prev];
          // Respect showAll state when adding placeholders
          return !showAll ? next.slice(0, 20) : next;
        });
      }
      // no delay — loop continues immediately (fast as possible)
    }

    console.log(`[PLACEHOLDERS] Successfully created ${need} sequential placeholders for job ${jobId}`);
  };

    // Create any missing placeholders for this job in ONE transaction,
    // then prepend them to the UI with ONE setState (fast & flicker-free).
    const createPlaceholdersFast = async (jobId, requestedCount) => {
        const count = Number(requestedCount || 1);
        if (!jobId || count <= 0) return;

        console.log(`[PLACEHOLDERS] Creating placeholders for job ${jobId}, count: ${count}, queue enabled: ${isFeatureEnabled('USE_DATABASE_QUEUE')}`);

        // Avoid duplicates if we're called more than once.
        const existing = await db.images
        .where('uid').equals(jobId)
        .and(i => !i.image || i.image === "")
        .count();
        const need = Math.max(0, count - existing);
        if (need === 0) return;

        const now = Date.now();
        const batch = Array.from({ length: need }, () => ({
        image: "",                    // placeholder
        seed: "",
        uid: jobId,
        model: "",
        inCollection: false,
        hash: md5(String(Math.floor(Math.random() * 1e8))), // temporary key
        online: apiState.online,
        tag: generateStatus.tag,
        requestedtimestamp: now,
        receivedtimestamp: now,
        dataType: "placeholder"
        }));

        // Insert all placeholders using database queue if enabled, otherwise use direct transaction
        let ids = [];
        if (isFeatureEnabled('USE_DATABASE_QUEUE')) {
            console.log(`[PLACEHOLDERS] Using database queue for ${need} placeholders`);
            ids = await databaseOperations.addImageBatch(batch);
        } else {
            console.log(`[PLACEHOLDERS] Using direct transaction for ${need} placeholders`);
            await db.transaction('rw', db.images, async () => {
                ids = await Promise.all(batch.map(obj => db.images.add(obj)));
            });
        }

        // Prepend them to the gallery in one go (only for "Newest"-style sorts).
        if (sortMethod === "Newest" || sortMethod === "New_requested") {
        const placed = batch.map((o, i) => ({ ...o, id: ids[i] }));
        setImageList(prev => {
            const next = [...placed, ...prev];
            // Respect showAll state when adding placeholders for batch
            return !showAll ? next.slice(0, 20) : next;
        });
        }

        console.log(`[PLACEHOLDERS] Successfully created ${ids.length} placeholders for job ${jobId}`);
    };

        // Create any missing placeholders for this job in ONE transaction,
    // then prepend them to the UI with ONE setState (fast & flicker-free).
    const replaceFailedPlaceholdersFast = async (requestedCount, failedImageID) => {
        const count = Number(requestedCount || 1);
        const failedJob = await db.images.where('id').equals(failedImageID).first();
        if (!failedImageID || count <= 0) return;

        console.log(`[REPLACE FAILED] Replacing failed placeholders, count: ${count}, failedImageID: ${failedImageID}, queue enabled: ${isFeatureEnabled('USE_DATABASE_QUEUE')}`);
        console.log('[REPLACE FAILED] failedJob: ', failedJob);

        if (count == 1) {
            console.log(`[REPLACE FAILED] Single job replacement for id ${failedJob.id}`);

            // Use database queue if enabled, otherwise direct update
            if (isFeatureEnabled('USE_DATABASE_QUEUE')) {
                await databaseOperations.updateImage(failedJob.id, {
                    dataType: "placeholder"
                });
            } else {
                await db.images.update(failedJob.id, {
                    dataType: "placeholder"
                });
            }
        } else if (count > 1) {
            const failedJobs = await db.images.where('uid').equals(failedJob.uid).and(i => !i.image || i.image === "").toArray();
            console.log(`[REPLACE FAILED] Multiple job replacement for ${failedJobs.length} jobs`);

            for (const job of failedJobs) {
                console.log(`[REPLACE FAILED] Updating job id ${job.id}`);

                // Use database queue if enabled, otherwise direct update
                if (isFeatureEnabled('USE_DATABASE_QUEUE')) {
                    await databaseOperations.updateImage(job.id, {
                        dataType: "placeholder"
                    });
                } else {
                    await db.images.update(job.id, {
                        dataType: "placeholder"
                    });
                }
            }
        }

        console.log(`[REPLACE FAILED] Successfully replaced failed placeholders`);
        refreshImages(false)
        
        // // Avoid duplicates if we’re called more than once.
        // const existing = await db.images
        // .where('uid').equals(jobId)
        // .and(i => !i.image || i.image === "")
        // .count();
        // const need = Math.max(0, count - existing);
        // if (need === 0) return;
    
        // const now = Date.now();
        // let batch

        // if (failedImageID) {
        //     batch = Array.from({ length: need }, () => ({
        //         dataType: "placeholder"
        //     }));
        // } else {
        //     batch = Array.from({ length: need }, () => ({
        //     image: "",                    // placeholder
        //     seed: "",
        //     uid: jobId,
        //     model: "",
        //     inCollection: false,
        //     hash: md5(String(Math.floor(Math.random() * 1e8))), // temporary key
        //     online: apiState.online,
        //     tag: generateStatus.tag,
        //     requestedtimestamp: now,
        //     receivedtimestamp: now,
        //     dataType: "placeholder"
        //     }));
        // }
    
        // // Insert all placeholders at once and get their auto IDs back.
        // let ids = [];
        // await db.transaction('rw', db.images, async () => {
        //     if (failedImageID) {
        //         ids = await Promise.all(batch.map(obj => {
        //             console.log('[CREATE PLACHOLDER] failedImageID obj: ', obj);
                    
        //             db.images.update(failedImageID, obj)
        //         }));
        //     } else {
        //         ids = await Promise.all(batch.map(obj => {
        //             console.log('[CREATE PLACHOLDER] normal obj: ', obj);
        //             db.images.add(obj)
        //         }));
        //     }
        // });
    
        // if (!failedImageID) {
        //     // Prepend them to the gallery in one go (only for "Newest"-style sorts).
        //     if (sortMethod === "Newest" || sortMethod === "New_requested") {
        //     const placed = batch.map((o, i) => ({ ...o, id: ids[i] }));
        //     setImageList(prev => {
        //         const next = [...placed, ...prev];
        //         return !showAll ? next.slice(0, 20) : next;
        //     });
        //     }
        // } else {
        //     refreshImages()
        // }
    };

    async function resolveCount(params = {}, selectedModel = null) {
        if (!params || typeof params !== "object") return 1;
        console.log('[RESOLVE COUNT] Starting ', params, 'for model:', selectedModel);

        // Step 1: Schema-based detection using style: 'count'
        if (selectedModel) {
            try {
                console.log('[RESOLVE COUNT] Attempting schema-based count detection for model:', selectedModel);
                const cosyflowVariables = await getCosyflowDataFromDB(selectedModel);

                if (cosyflowVariables) {
                    console.log('[RESOLVE COUNT] Found cosyflow variables, searching for style: count fields');
                    // Look for fields with style: 'count'
                    for (const [fieldName, fieldConfig] of Object.entries(cosyflowVariables)) {
                        if (fieldConfig.style === 'count') {
                            console.log(`[RESOLVE COUNT] Found schema-based count field: ${fieldName} with style: 'count'`);

                            // Handle field names with 'params.' prefix
                            let actualFieldName = fieldName;
                            if (fieldName.startsWith('params.')) {
                                actualFieldName = fieldName.replace('params.', '');
                                console.log(`[RESOLVE COUNT] Stripped params prefix: ${fieldName} -> ${actualFieldName}`);
                            }

                            if (params[actualFieldName] !== undefined) {
                                console.log(`[RESOLVE COUNT] Using schema-detected count field "${actualFieldName}" with value: ${params[actualFieldName]}`);
                                return params[actualFieldName];
                            }
                        }
                    }
                    console.log('[RESOLVE COUNT] No fields with style: count found in schema or no values provided');
                } else {
                    console.log('[RESOLVE COUNT] No cosyflow variables found for model:', selectedModel);
                }
            } catch (error) {
                console.error('[RESOLVE COUNT] Error during schema-based detection:', error);
            }
        } else {
            console.log('[RESOLVE COUNT] No selectedModel provided, skipping schema-based detection');
        }

        // Step 2: Explicit priority keys (fallback)
        const priorityKeys = ["batch_size", "number_of_videos" , "count", "n"];
        for (const key of priorityKeys) {
          if (params[key] !== undefined) {
            console.log(`[RESOLVE COUNT] Found priority key: ${key} with value: ${params[key]}`);
            return params[key];
          }
        }

        // Step 3: Fuzzy match: look for any key that includes "count"
        for (const [key, value] of Object.entries(params)) {
          if (key.toLowerCase().includes("count")) {
            console.log(`[RESOLVE COUNT] Found fuzzy match: ${key} with value: ${value}`);
            return value;
          }
        }

        // Step 4: Default fallback
        console.log('[RESOLVE COUNT] Using default fallback value: 1');
        return 1;
      }

    /**
     * Sends the prompt to the API stores it in the global state
     * @param promptData The full prompt object including params
     */
    // Add a flag to prevent duplicate calls
    const isGeneratingRef = useRef(false);
    
    const handleGenerate = (promptData, failedImage, failedImageID) => {
        console.log('[GENERATE_FLOW] Generate button clicked', {
            timestamp: Date.now(),
            promptData: JSON.parse(JSON.stringify(promptData)), // Deep copy to see original
            failedImage,
            failedImageID,
            currentStatus: generateStatus.status,
            currentImageCount: imageList.length,
            isGenerating: isGeneratingRef.current
        });
        
        // Prevent duplicate calls
        if (isGeneratingRef.current) {
            console.warn('[GENERATE_FLOW] Already generating, ignoring duplicate call');
            return;
        }
        
        // Set flag immediately
        isGeneratingRef.current = true;
        
        // Clear flag after a delay (in case of errors)
        setTimeout(() => {
            isGeneratingRef.current = false;
        }, 5000);
        
        console.log('[GENERATE_FLOW] Prompt structure at start:', JSON.stringify(promptData.prompt, null, 2));
        
        console.log("Is it a failed image regen? ",failedImage);
        console.log("The failed image ID ", failedImageID);
        setFailedImage(failedImage);
        setFailedImageID(failedImageID);
        let countValue = 0;
        getUUID().then(async UUID => {
            promptData.accountNo = UUID;
            
            // Get the selected model name from promptData
            let selectedModel = null;
            if (promptData.prompt && promptData.prompt.model) {
                selectedModel = promptData.prompt.model;
            } else if (promptData.prompt && promptData.prompt.cosyflow) {
                selectedModel = promptData.prompt.cosyflow;
            }
            
            // Fetch cosyflow data from IndexedDB if model is found
            if (selectedModel) {
                console.log('[GENERATE_FLOW] Fetching cosyflow data for model:', selectedModel);
                const cosyflowVariables = await getCosyflowDataFromDB(selectedModel);
                
                if (cosyflowVariables) {
                    console.log('[GENERATE_FLOW] Applying cosyflow values to prompt data');
                    console.log('[GENERATE_FLOW] Current user params:', promptData.prompt?.params);
                    
                    // Extract default values from cosyflow variables
                    const cosyflowDefaults = {};
                    for (const [key, variable] of Object.entries(cosyflowVariables)) {
                        if (variable.default !== undefined) {
                            cosyflowDefaults[key] = variable.default;
                        }
                    }
                    console.log('[GENERATE_FLOW] Cosyflow defaults extracted:', cosyflowDefaults);
                    
                    // Skip cosyflow merging for the new structure where params is at root level
                    // The prompt structure from PromptHeader already has all the necessary fields
                    console.log('[GENERATE_FLOW] Using prompt structure as-is - cosyflow defaults already applied in PromptHeader');
                    
                    // Process b64data fields - convert images to base64 if needed
                    console.log('[GENERATE_FLOW] Checking for b64data fields to convert');
                    
                    // Track if we've used the inputImage for the first b64data field
                    let firstB64DataFieldProcessed = false;
                    
                    for (const [key, variable] of Object.entries(cosyflowVariables)) {
                        if (variable.type === 'b64data') {
                            console.log(`[GENERATE_FLOW] Found b64data field: ${key}`);
                            
                            let fieldValue = null;
                            let fieldPath = null;
                            
                            // Determine the field location and path
                            if (key.startsWith('params.')) {
                                const paramKey = key.replace('params.', '');
                                fieldValue = promptData.prompt?.params?.[paramKey];
                                fieldPath = ['prompt', 'params', paramKey];
                            } else {
                                fieldValue = promptData.prompt?.[key];
                                fieldPath = ['prompt', key];
                            }
                            
                            console.log(`[GENERATE_FLOW] Current value for ${key}:`, fieldValue ? `${fieldValue.substring(0, 50)}...` : fieldValue);
                            console.log(`[GENERATE_FLOW] Default value for ${key}:`, variable.default ? `${variable.default.substring(0, 50)}...` : variable.default);
                            
                            // Special handling for mask_image
                            if (key.includes('mask_image')) {
                                console.log(`[GENERATE_FLOW] MASK_IMAGE SPECIAL CHECK:`, {
                                    hasValue: !!fieldValue,
                                    valueLength: fieldValue?.length || 0,
                                    defaultLength: variable.default?.length || 0,
                                    valuesMatch: fieldValue === variable.default,
                                    valueStart: fieldValue?.substring(0, 100),
                                    defaultStart: variable.default?.substring(0, 100)
                                });
                            }
                            
                            // Check if the field has a default/placeholder value
                            // NEVER clear mask_image, even if it matches default
                            if (key.includes('mask_image')) {
                                console.log(`[GENERATE_FLOW] Skipping default check for mask_image - keeping value as-is`);
                                console.log(`[GENERATE_FLOW] Converting ${key} to base64`);
                                const base64Value = await convertToBase64(fieldValue);
                                if (base64Value) {
                                    setNestedValue(promptData, fieldPath, base64Value);
                                    console.log(`[GENERATE_FLOW] Successfully converted ${key} to base64`);
                                } else {
                                    console.warn(`[GENERATE_FLOW] Failed to convert ${key} to base64`);
                                }
                            } else if (fieldValue && fieldValue === variable.default) {
                                console.log(`[GENERATE_FLOW] Field ${key} has default value: ${variable.default}`);
                                
                                // For the first b64data field, try to use inputImage from Generate component
                                if (!firstB64DataFieldProcessed && inputImage.image) {
                                    console.log(`[GENERATE_FLOW] Using inputImage for first b64data field: ${key}`);
                                    setNestedValue(promptData, fieldPath, inputImage.image);
                                    fieldValue = inputImage.image;
                                    firstB64DataFieldProcessed = true;
                                } else {
                                    // Clear default value to prevent sending placeholders
                                    console.log(`[GENERATE_FLOW] Clearing default value for ${key}`);
                                    setNestedValue(promptData, fieldPath, null);
                                    fieldValue = null;
                                }
                            }
                            
                            // Convert to base64 if we have a non-null, non-default value
                            if (fieldValue && fieldValue !== variable.default) {
                                console.log(`[GENERATE_FLOW] Converting ${key} to base64`);
                                const base64Value = await convertToBase64(fieldValue);
                                if (base64Value) {
                                    setNestedValue(promptData, fieldPath, base64Value);
                                    console.log(`[GENERATE_FLOW] Successfully converted ${key} to base64`);
                                } else {
                                    console.warn(`[GENERATE_FLOW] Failed to convert ${key} to base64`);
                                }
                            }
                        }
                    }
                    
                    console.log('[GENERATE_FLOW] Final prompt data after cosyflow merge:', {
                        model: selectedModel,
                        params: promptData.prompt?.params,
                        cosyflowApplied: true
                    });
                } else {
                    console.log('[GENERATE_FLOW] No cosyflow data found, using values as-is:', {
                        model: selectedModel,
                        params: promptData.prompt?.params
                    });
                }
            }
            
            // Check seed value in nested structure
            if (promptData.prompt && (promptData.prompt.seed === "" || promptData.prompt.seed_value === "")) {
                setSameSeed(false);
            } else {
                setSameSeed(true);
            }
    
            let ip = '';
            try {
                const response = await fetch('https://api.ipify.org');
                ip = await response.text();
                console.log(ip)
            } catch (error) {
                console.error(error)
            }
    
            promptData.ip_address = ip;
            promptData.source = electronBridge.isElectron ? 'PWA' : 'PWeb';
            promptData.version = process.env.REACT_APP_VERSION;

            // Only inject test model on first attempt, not on retries
            // if (!modelUnavTest && !promptData._isRetry) {
            //     console.log('[TEST MODE] Injecting test model for first attempt');
            //     console.log('PROMPTDATA: ', promptData ,' | modelUnavTest: ', modelUnavTest);
                
            //     promptData.prompt.cosyflow = "Testing"
            //     promptData.prompt.params.model = "Testing"
            //     setModelUnavTest(true)
            // }

            // const offlineMode = await getActiveOfflineMode(apiState);
            // console.log("[HANDLE GENERATE] Offline Mode: ", offlineMode);

            if (!apiState.online && promptData.prompt.cosyflow.includes('(cosy)')) {
                promptData = promptData.prompt
            }    


            // eventBus.emit('request_pending');
            // generateStatusDispatch({type: 'ready'});
            // return;
            // The structure from PromptHeader already has everything in prompt object
            console.log('[GENERATE_FLOW] Using nested structure as-is from PromptHeader:', JSON.stringify(promptData, null, 2));

            // Look up server for the selected model (for unified offline models support)
            const modelName = promptData.prompt?.cosyflow ||
                             promptData.prompt?.params?.model ||
                             promptData.prompt?.model ||
                             promptData?.cosyflow ||
                             promptData?.model;

            console.log(`[SERVER ROUTING] Extracted model name: "${modelName}" from promptData:`, promptData);
            const serverInfo = modelName ? await getServerForModel(modelName) : null;

            console.log(`[SERVER ROUTING] Routing model "${modelName}" to ${serverInfo}`);
            let targetServerUrl = serverInfo ? serverInfo.serverUrl : null;

            if (apiState.online) {
                targetServerUrl = await getApiBaseUrl();
            } else {
                if (targetServerUrl) {
                    console.log(`[SERVER ROUTING] Routing model "${modelName}" to ${serverInfo.serverType} server: ${targetServerUrl}`);
                } else {
                    console.log(`[SERVER ROUTING] Using default server for model "${modelName}"`);
                }
            }

            console.log("[GENERATE_FLOW] Prompt Data before sending to API: ", promptData);

            generatePrompt(promptData, apiState, generateStatusDispatch, targetServerUrl)
                .then(async data => {
                    console.log("Data received from API job request: ", data);
                    setRedoPromptUid("");
                    if (data.error) {
                        // Clear the generating flag on error
                        isGeneratingRef.current = false;
                        
                        const errorMessage = typeof data.error === 'string' ? data.error : JSON.stringify(data.error);
                        
                        // Handle "Model unavailable" error specially
                        if (errorMessage === "Model unavailable") {
                            console.log('[MODEL FALLBACK] Model unavailable error detected');
                            
                            // Check if this is already a retry
                            const attemptedModels = promptData._attemptedModels || [];
                            const currentModel = promptData.prompt?.params?.model || 
                                                promptData.prompt?.model || 
                                                promptData.prompt?.cosyflow || '';
                            
                            if (attemptedModels.length < 5) {
                                // Emit event to PromptHeader for model fallback
                                eventBus.emit('model_unavailable', {
                                    currentModel: currentModel,
                                    attemptedModels: attemptedModels,
                                    originalPrompt: promptData
                                });
                                return; // Don't show error yet
                            } else {
                                // All retries exhausted
                                console.error('[MODEL FALLBACK] All retry attempts failed');
                                generateStatusDispatch({ 
                                    type: 'error', 
                                    message: 'All available models are currently unavailable. Please try again later.' 
                                });
                            }
                        } else if (errorMessage.toLowerCase() === "insufficient credits" || errorMessage.toLowerCase() === "out of credits") {
                            Swal.fire({
                                title: "<strong>Out Of Credits? Go Unlimited</strong>",
                                html: `<p class="text-base mb-4">You can either top up for online use or download Promptus once to generate offline forever - no monthly fees.</p>
                                      <button id="topUpNowBtn" class="rounded-lg bg-yellow py-3 px-5 shadow-xl font-bold text-xl text-white uppercase">Top Up Now</button>
                                      <button id="goOfflineBtn" class="rounded-lg bg-red-600 py-3 px-5 shadow-xl font-bold text-xl text-white uppercase mt-3">Go Offline (One-Time Purchase)</button>`,
                                icon: "error",
                                showConfirmButton: false,
                                
                                didOpen: () => {
                                    const btn = document.getElementById("topUpNowBtn");
                                    const nxtbtn = document.getElementById("goOfflineBtn");
                                    if (btn) btn.addEventListener("click", () => openPromptusCreditsStore('payments'));
                                    if (nxtbtn) nxtbtn.addEventListener("click", () => openPromptusCreditsStore('subscriptions'));
                                }
                            });
                            generateStatusDispatch({type: 'ready'});
                            eventBus.emit('request_pending');
                            return; // Don't show error yet
                        } else if (errorMessage.toLowerCase().includes('moderation')){
                            Swal.fire({
                                title: 'Checked For Safety',
                                text: 'Some words in your prompt may trigger content filters. Edit your prompt or generate offline',
                                icon: "warning",
                                confirmButtonText: "Enhance Prompt",
                                confirmButtonColor: "#190A4E",
                                showDenyButton: true,
                                denyButtonText: 'Go Offline',
                                denyButtonColor: '#FFB02E'
                            }).then((result) => {
                                if (result.isConfirmed) {
                                    eventBus.emit('enhancePromptTrue');
                                }
                                if (result.isDenied) {
                                    eventBus.emit('offlineToggleTrue');
                                }
                            });
                            generateStatusDispatch({type: 'ready'});
                            eventBus.emit('request_pending');
                            return; // Don't show error yet
                        } else {
                            eventBus.emit('request_pending');
                            generateStatusDispatch({ type: 'error', message: data.error });
                        } 
                    }
                    if (data.uid || data.prompt_id) {
                        const jobId = data.uid || data.prompt_id;
    
                        // Backward compatibility: single job state
                        // generateStatusDispatch({type: 'generate', uid: jobId, count: promptData.prompt?.params?.count || 1});
                        // Multi-job state
                        
                        // await createPlaceholdersSequential(jobId, promptData.prompt?.params?.count || 1);                       

                        if (promptData.prompt?.params) {
                            countValue = await resolveCount(promptData.prompt?.params, promptData.prompt.cosyflow);
                        } else if (promptData?.prompt) {
                            countValue = await resolveCount(promptData.prompt, promptData.prompt.cosyflow);
                        } else  {
                            countValue = await resolveCount(promptData, promptData.cosyflow);
                        }

                        console.log('TESTIINGWHYOEHIAWHIOEQWQWDHIO: ', countValue);
                        
                        if (failedImageID) {
                            await replaceFailedPlaceholdersFast(countValue, failedImageID);
                        } else {
                            await createPlaceholdersFast(jobId, countValue, failedImageID);
                        }
                        setModelUnavTest(false)
                        
                        console.log('[GENERATE_FLOW] Job ID received', {
                            timestamp: Date.now(),
                            jobId: jobId,
                            previousStatus: generateStatus.status,
                            currentImageCount: imageList.length,
                            totalStoredImages: totalStoredImages,
                            stack: new Error().stack
                        });
                        
                        // Generate and save new random seed for next generation
                        if (selectedModel) {
                            console.log('[GENERATE_FLOW] Generating new random seed for next generation');
                            const newRandomSeed = generateRandomSeed();
                            console.log('[GENERATE_FLOW] New random seed generated:', newRandomSeed);
                            
                            // Update the cosyflows table with the new seed
                            updateCosyflowSeed(selectedModel, newRandomSeed).then(() => {
                                console.log('[GENERATE_FLOW] Successfully saved new seed to cosyflows table');
                            }).catch(error => {
                                console.error('[GENERATE_FLOW] Error saving new seed:', error);
                            });
                        }
                        
                        // Note: Prompt history is now handled in PromptHeader.jsx when the user submits
    
                        // Add collection entry using database queue if enabled
                        const addCollectionPromise = isFeatureEnabled('USE_DATABASE_QUEUE')
                            ? databaseOperations.addCollection({
                                uid: jobId,
                                prompt: promptData,
                                queue: [],
                                timestamp: Date.now(),
                            })
                            : db.collection.add({
                                uid: jobId,
                                prompt: promptData,
                                queue: [],
                                timestamp: Date.now(),
                            });

                        addCollectionPromise.then(async() => {
                            localStorage.setItem('isCancelling', false);
                            if (failedImage) generateStatusDispatch({type: 'failedImage'});
                            setSameSeedDate(Date.now());
    
                            if (promptData.prompt && promptData.prompt.params) {
                                // generateStatusDispatch({type: 'generate', uid: jobId, count: promptData.prompt.params.count});
                                // Multi-job state
                                generateStatusDispatch({
                                    type: 'addJob',
                                    uid: jobId,
                                    count: countValue,
                                    serverUrl: targetServerUrl,
                                    modelName: modelName,
                                    serverType: serverInfo?.serverType
                                });
                                // await createPlaceholdersSequential(jobId, countValue);
                                eventBus.emit('request_pending');
                                // Save to new multi-job format
                                savePendingJob(jobId, countValue);
                                // Keep legacy format for backward compatibility
                                localStorage.setItem('remember_workload', JSON.stringify({ active: true, uid: jobId, count: countValue, totalImage: 0, num: 0 }));
                            } else {
                                // generateStatusDispatch({type: 'generate', uid: jobId, count: 1});
                                // Multi-job state
                                generateStatusDispatch({
                                    type: 'addJob',
                                    uid: jobId,
                                    count: countValue,
                                    serverUrl: targetServerUrl,
                                    modelName: modelName,
                                    serverType: serverInfo?.serverType
                                });
                                // await createPlaceholdersSequential(jobId, countValue);
                                eventBus.emit('request_pending');
                                // Save to new multi-job format
                                savePendingJob(jobId, countValue);
                                // Keep legacy format for backward compatibility
                                localStorage.setItem('remember_workload', JSON.stringify({ active: true, uid: jobId, count: countValue, totalImage: 0, num: 0 }));
                            }
                            
                            // Clear the generating flag after successful setup
                            isGeneratingRef.current = false;
                        });
                    }
                })
                .catch(error => {
                    console.error('[GENERATE_FLOW] Error in handleGenerate:', error);
                    eventBus.emit('request_pending');
                    generateStatusDispatch({type: 'ready'});
                    // Clear the generating flag on error
                    isGeneratingRef.current = false;
                });
        });
    };

    /**
     * Clean up any remaining placeholders after all jobs complete
     * Converts placeholders to failed images by clearing their dataType
     */
    const cleanupRemainingPlaceholders = async (uid = null) => {
        const context = uid ? `for job ${uid}` : 'for all jobs';
        console.log(`[CLEANUP] Starting placeholder cleanup ${context}...`);

        try {
            // First, let's check the current state of all jobs
            const currentJobs = generateStatus.jobs || {};
            const allJobEntries = Object.entries(currentJobs);
            console.log(`[CLEANUP] Current jobs state:`, allJobEntries.map(([id, job]) =>
                `${id}: ${job.status} (${job.totalImages || 0}/${job.count || 0} images)`
            ));

            // Check if we should actually run cleanup yet
            if (!uid) {
                // For global cleanup, make sure there are really no active jobs
                const activeJobs = allJobEntries.filter(([id, job]) =>
                    job.status === 'generating' || job.status === 'pending'
                );

                if (activeJobs.length > 0) {
                    console.log(`[CLEANUP] Skipping cleanup - ${activeJobs.length} active jobs still running:`,
                        activeJobs.map(([id, job]) => `${id}:${job.status}`));
                    return;
                }

                // Check if any jobs are marked 'done' but might still be processing
                const doneJobs = allJobEntries.filter(([id, job]) => job.status === 'done');
                console.log(`[CLEANUP] Found ${doneJobs.length} completed jobs`);
            }

            // Build the query based on whether uid is provided
            let placeholdersQuery = db.images
                .filter(img =>
                    (!img.image || img.image === "") &&
                    (img.dataType === "placeholder")
                );

            // If uid is provided, filter by that specific job uid
            let placeholders;
            if (uid) {
                placeholders = await placeholdersQuery
                    .and(img => img.uid === uid)
                    .toArray();
            } else {
                placeholders = await placeholdersQuery.toArray();
            }

            console.log(`[CLEANUP] Found ${placeholders.length} placeholders to potentially clean up ${context}`);

            if (placeholders.length > 0) {
                // Group placeholders by job UID for better logging
                const placeholdersByJob = placeholders.reduce((acc, ph) => {
                    acc[ph.uid] = (acc[ph.uid] || 0) + 1;
                    return acc;
                }, {});

                console.log(`[CLEANUP] Placeholders by job:`, placeholdersByJob);

                // Only cleanup placeholders for jobs that are truly done OR orphaned
                let placeholdersToCleanup = placeholders;
                if (!uid) {
                    // For global cleanup, clean placeholders from:
                    // 1. Jobs that are completely done
                    // 2. Jobs that no longer exist (orphaned placeholders)

                    const allExistingJobUIDs = allJobEntries.map(([id, job]) => id);
                    const doneJobUIDs = allJobEntries
                        .filter(([id, job]) => job.status === 'done')
                        .map(([id, job]) => id);

                    // Find placeholders from completed jobs
                    const completedPlaceholders = placeholders.filter(ph => doneJobUIDs.includes(ph.uid));

                    // Find orphaned placeholders (job no longer exists)
                    const orphanedPlaceholders = placeholders.filter(ph => !allExistingJobUIDs.includes(ph.uid));

                    placeholdersToCleanup = [...completedPlaceholders, ...orphanedPlaceholders];

                    console.log(`[CLEANUP] Found ${completedPlaceholders.length} placeholders from completed jobs`);
                    console.log(`[CLEANUP] Found ${orphanedPlaceholders.length} orphaned placeholders from non-existent jobs:`,
                        orphanedPlaceholders.map(ph => ph.uid));
                    console.log(`[CLEANUP] Total placeholders to cleanup: ${placeholdersToCleanup.length}`);
                }

                if (placeholdersToCleanup.length > 0) {
                    console.log(`[CLEANUP] Converting ${placeholdersToCleanup.length} placeholders to failed images...`);

                    // Update each placeholder to be a failed image
                    const allExistingJobUIDs = allJobEntries.map(([id, job]) => id);

                    for (const placeholder of placeholdersToCleanup) {
                        // Use database queue if enabled for cleanup operations
                        if (isFeatureEnabled('USE_DATABASE_QUEUE')) {
                            await databaseOperations.updateImage(placeholder.id, {
                                dataType: "", // Empty dataType indicates failed image
                                image: ""     // Ensure image is empty
                            });
                        } else {
                            await db.images.update(placeholder.id, {
                                dataType: "", // Empty dataType indicates failed image
                                image: ""     // Ensure image is empty
                            });
                        }

                        const isOrphaned = !allExistingJobUIDs.includes(placeholder.uid);
                        const reason = isOrphaned ? 'ORPHANED (job no longer exists)' : 'COMPLETED job';
                        console.log(`[CLEANUP] Converted placeholder ${placeholder.id} from ${reason} ${placeholder.uid} (hash: ${placeholder.hash}) to failed image`);
                    }

                    console.log(`[CLEANUP] Successfully converted ${placeholdersToCleanup.length} placeholders to failed images ${context}`);

                    // Refresh the image list to show the updates
                    await refreshImages(false);
                } else {
                    console.log(`[CLEANUP] No placeholders ready for cleanup ${context} - all placeholders are from active jobs`);
                }
            } else {
                console.log(`[CLEANUP] No remaining placeholders found ${context}`);
            }
        } catch (error) {
            console.error(`[CLEANUP] Error during placeholder cleanup ${context}:`, error);
        }
    };

    const findFailedMatches = (failedImages, undeliveredImages) => {
        console.log("Looking for failed matches to deliver");
        
        return undeliveredImages.filter(largeItem => 
            failedImages.some(smallItem => smallItem.uid === largeItem.jobUid)
        );
      };

    const removeFailedMatches = (failedImages, undeliveredImages) => {
        console.log("Removing failed matches from undelivered images");
        
        return undeliveredImages.filter(largeItem => 
            !failedImages.some(smallItem => smallItem.uid === largeItem.jobUid)
        );
    };   

    /**
     * Get the total stored images from the database
     * @returns {Promise<void>}
     */
    const getTotalStoredImages = async () => {
        console.log('[GALLERY_REFRESH] getTotalStoredImages called');

        try {
            // Get image count
            const count = await db.images.count();
            setTotalStoredImages(count);

            // Get settings
            const settings = await db.settings.toArray();
            console.log("Settings: ", settings);
            settings.forEach(setting => {
                if (setting.key === "local_directory") {
                    setLocalDirectory(setting.value);
                }
            });

            // Refresh images (let refreshImages handle its own debouncing)
            await refreshImages(true);

            console.log('[GALLERY_REFRESH] Gallery initialization completed successfully');
        } catch (error) {
            console.error('[GALLERY_REFRESH] Error during gallery initialization:', error);
            // Ensure we still show something even on error
            setImageList([]);
        }
    }

    /**
     * Refresh the images and action the pagination
     * @returns {Promise<void>}
     */
    // const refreshImages = async () => {
    //     let playgroundImages = await db.images.reverse().toArray();
    //     if (!showAll) {
    //         await db.images.reverse().offset(page * 20).limit(20).toArray((images) => setImageList(images));
    //     } else {
    //         await db.images.reverse().toArray((images) => setImageList(images));
    //     }
    
    //     if (category !== "All") {
    //         if (category === "Public" || category === "Private") {
    //             playgroundImages = playgroundImages.filter(image => image.public === (category === "Public"));
    //         } else if (category === "Non-Tagged") {
    //             playgroundImages = playgroundImages.filter(image => isUndefined(image.tag) || image.tag === "");
    //         } else {
    //             playgroundImages = playgroundImages.filter(image => !isUndefined(image.tag) && image.tag.toLowerCase().includes(category.toLowerCase()));
    //         }
    //         const startIndex = page * 20;
    //         const endIndex = startIndex + 20;
    //         const slicedImages = playgroundImages.slice(startIndex, endIndex);
        
    //         setImageList(slicedImages);
    //     }
    // }

    /**
     * Append a single new image to the current imageList if it should be visible
     * @param {string} imageId - The ID or UID of the image to add
     * @param {string} imageHash - The hash of the image (optional)
     */
    const appendNewImage = async (imageId, imageHash) => {
        const appendStartTime = Date.now();
        console.log('[APPEND_NEW_IMAGE] ========== START ==========');
        console.log('[APPEND_NEW_IMAGE] appendNewImage called', {
            timestamp: appendStartTime,
            imageId,
            imageHash,
            currentListLength: imageList.length,
            currentStatus: generateStatus.status,
            isGalleryInitializing,
            totalStoredImages,
            category,
            sortMethod,
            page,
            showAll,
            refreshSeqCurrent: refreshSeqRef.current
        });

        try {
            // Get the new image from database
            console.log('[APPEND_NEW_IMAGE] Querying database for image...');
            let newImage;
            if (imageHash) {
                newImage = await db.images.where('hash').equals(imageHash).first();
            } else {
                newImage = await db.images.where('id').equals(imageId).first();
            }

            if (!newImage) {
                console.log("[APPEND_NEW_IMAGE] ERROR: New image not found in database");
                return;
            }

            console.log('[APPEND_NEW_IMAGE] Found image in database:', {
                id: newImage.id,
                uid: newImage.uid,
                hash: newImage.hash,
                hasImage: !!newImage.image,
                imageLength: newImage.image?.length || 0,
                timestamp: newImage.timestamp,
                tag: newImage.tag
            });

            // Special handling for empty gallery (first image)
            if (imageList.length === 0) {
                console.log('[APPEND_NEW_IMAGE] ========== EMPTY GALLERY DETECTED ==========');
                console.log('[APPEND_NEW_IMAGE] State before updates:', {
                    imageListLength: imageList.length,
                    totalStoredImages,
                    isGalleryInitializing,
                    generateStatusLoading: generateStatus.loading
                });

                console.log('[APPEND_NEW_IMAGE] Setting imageList to [newImage]...');
                setImageList([newImage]);
                console.log('[APPEND_NEW_IMAGE] Setting isGalleryInitializing to false...');
                setIsGalleryInitializing(false);
                console.log('[APPEND_NEW_IMAGE] Setting totalStoredImages to 1...');
                setTotalStoredImages(1);

                console.log('[APPEND_NEW_IMAGE] State updates dispatched, calling refreshImages(true)...');
                refreshImages(true);

                console.log('[APPEND_NEW_IMAGE] ========== EMPTY GALLERY HANDLING COMPLETE ==========');
                return;
            }

            console.log('[APPEND_NEW_IMAGE] ========== NORMAL APPEND PATH ==========');
            console.log('[APPEND_NEW_IMAGE] Existing images in gallery:', {
                imageListLength: imageList.length,
                firstImageId: imageList[0]?.id,
                firstImageUid: imageList[0]?.uid,
                firstImageHasData: !!imageList[0]?.image,
                firstImageIsPlaceholder: imageList[0]?.image === "",
                firstImageDataLength: imageList[0]?.image?.length || 0,
                totalStoredImages,
                newImageId: newImage.id,
                newImageUid: newImage.uid,
                newImageHasData: !!newImage.image
            });

            // Check if image matches current filters
            let shouldDisplay = true;
            
            // Check category filter
            if (category !== "All") {
                if (category === "Non-Tagged") {
                    shouldDisplay = !newImage.tag || newImage.tag === "";
                } else if (category === "Failed") {
                    shouldDisplay = newImage.image === "";
                } else {
                    shouldDisplay = newImage.tag && newImage.tag.toLowerCase().includes(category.toLowerCase());
                }
            }
            
            // Check sort method specific filters
            if (sortMethod === "Non-Tagged-Asc" || sortMethod === "Non-Tagged-Desc") {
                shouldDisplay = shouldDisplay && (!newImage.tag || newImage.tag === "");
            } else if (sortMethod === "AIHorde-Images-Asc" || sortMethod === "AIHorde-Images-Desc") {
                shouldDisplay = shouldDisplay && newImage.model && newImage.model.toLowerCase().startsWith('ai-horde');
            }
            
            if (!shouldDisplay) {
                console.log("New image doesn't match current filters");
                return;
            }

            // For pagination, only add if we're on page 1 and sorting by newest
            if (!showAll && page !== 0 && (sortMethod === "Newest" || sortMethod === "New_requested")) {
                console.log("Not on first page, skipping append");
                return;
            }

            // Add to the appropriate position based on sort
            setImageList(prevList => {
                console.log('[APPEND_NEW_IMAGE] ========== UPDATING IMAGE LIST ==========');
                console.log('[APPEND_NEW_IMAGE] Before update:', {
                    prevListLength: prevList.length,
                    totalStoredImages,
                    searchingForId: newImage.id,
                    searchingForHash: imageHash
                });

                // If the item already exists, replace it in place (keeps position stable)
                const idx = prevList.findIndex(
                    img => img.id === newImage.id || (imageHash && img.hash === imageHash)
                );
                if (idx !== -1) {
                    console.log('[APPEND_NEW_IMAGE] FOUND EXISTING IMAGE - REPLACING:', {
                        foundAtIndex: idx,
                        existingImageId: prevList[idx].id,
                        existingImageUid: prevList[idx].uid,
                        existingImageHasData: !!prevList[idx].image,
                        existingImageIsPlaceholder: prevList[idx].image === "",
                        newImageHasData: !!newImage.image,
                        willUpdateTotalStoredImages: prevList[idx].image === "" && newImage.image !== ""
                    });

                    const updated = prevList.slice();
                    const wasPlaceholder = updated[idx].image === "";
                    updated[idx] = { ...updated[idx], ...newImage };

                    // If we're replacing a placeholder with real data, update totalStoredImages
                    if (wasPlaceholder && newImage.image !== "") {
                        console.log('[APPEND_NEW_IMAGE] PLACEHOLDER REPLACED WITH REAL IMAGE - UPDATING COUNT');
                        setTimeout(() => setTotalStoredImages(prev => {
                            console.log('[APPEND_NEW_IMAGE] Updating totalStoredImages from', prev, 'to', prev + 1);
                            return prev + 1;
                        }), 0);
                    }

                    return updated;
                }

                // For "Newest" sorts, prepend to the beginning
                if (sortMethod === "Newest" || sortMethod === "New_requested") {
                    const next = [newImage, ...prevList];
                    // Only limit to 20 if showAll is false AND we're in paginated mode
                    if (!showAll && next.length > 20) {
                        next.pop();
                    }
                    return next;
                }

                // For "Oldest" sorts, append to the end
                if (sortMethod === "Oldest" || sortMethod === "Old_requested") {
                    const next = [...prevList, newImage];
                    // Only limit to 20 if showAll is false AND we're in paginated mode
                    if (!showAll && next.length > 20) {
                        next.shift(); // Remove from beginning for oldest sort
                    }
                    return next;
                }

                // For other sort methods during active jobs, try to insert in correct position
                // without forcing a full refresh which disrupts the showAll state
                console.log('[APPEND_NEW_IMAGE] Special sort detected, attempting smart insert', {
                    timestamp: Date.now(), 
                    sortMethod,
                    showAll
                });
                
                // For Non-Tagged sorts, only add if image is non-tagged
                if (sortMethod === "Non-Tagged-Asc" || sortMethod === "Non-Tagged-Desc") {
                    if (!newImage.tag || newImage.tag === "") {
                        const next = sortMethod === "Non-Tagged-Desc" ? 
                            [newImage, ...prevList] : [...prevList, newImage];
                        if (!showAll && next.length > 20) {
                            sortMethod === "Non-Tagged-Desc" ? next.pop() : next.shift();
                        }
                        return next;
                    }
                }
                
                // For AIHorde sorts, only add if it's an AIHorde image
                if (sortMethod === "AIHorde-Images-Asc" || sortMethod === "AIHorde-Images-Desc") {
                    if (newImage.model && newImage.model.toLowerCase().startsWith('ai-horde')) {
                        const next = sortMethod === "AIHorde-Images-Desc" ? 
                            [newImage, ...prevList] : [...prevList, newImage];
                        if (!showAll && next.length > 20) {
                            sortMethod === "AIHorde-Images-Desc" ? next.pop() : next.shift();
                        }
                        return next;
                    }
                }
                
                // If we can't smart insert, just add to beginning and let next proper refresh sort it
                const next = [newImage, ...prevList];
                if (!showAll && next.length > 20) {
                    next.pop();
                }
                return next;
            });
            
            console.log("[APPEND_NEW_IMAGE] ========== APPEND COMPLETE ==========");
            console.log("[APPEND_NEW_IMAGE] Successfully appended new image to list", {
                currentImageListLength: imageList.length,
                totalStoredImages,
                completedAt: Date.now()
            });
        } catch (error) {
            console.error("[APPEND_NEW_IMAGE] Error appending new image:", error);
            // Fall back to full refresh on error
            refreshImages(true);
        }
    };

    // DEBOUNCED refreshImages wrapper to prevent multiple concurrent calls
    const refreshImages = (forceRefresh = false, isShowAllChange = false) => {
        const callId = Math.random().toString(36).substr(2, 9);
        console.log(`[GALLERY_PERF] refreshImages called [${callId}]: force=${forceRefresh}, showAllChange=${isShowAllChange}`);

        // REQUEST DEDUPLICATION: Create request signature and check for recent duplicates
        const requestKey = `${page}-${showAll}-${category}-${sortMethod}-${forceRefresh}-${isShowAllChange}`;
        const now = Date.now();
        const recentRequest = recentRequests.current.get(requestKey);

        if (recentRequest && !forceRefresh && !isShowAllChange) {
            const timeSinceLastRequest = now - recentRequest.timestamp;
            if (timeSinceLastRequest < REQUEST_DEDUPE_MS) {
                console.log(`[GALLERY_PERF] Duplicate request detected [${callId}], ignoring (${timeSinceLastRequest}ms ago)`);
                return;
            }
        }

        // Clean up old requests (older than deduplication window)
        for (const [key, request] of recentRequests.current) {
            if (now - request.timestamp > REQUEST_DEDUPE_MS * 2) {
                recentRequests.current.delete(key);
            }
        }

        // Record this request
        recentRequests.current.set(requestKey, { timestamp: now, callId });

        // Store the latest call arguments
        pendingRefreshArgs.current = { forceRefresh, isShowAllChange, callId };

        // If refresh is already scheduled, just update args - don't create new timer
        if (isRefreshScheduled.current && !forceRefresh && !isShowAllChange) {
            console.log(`[GALLERY_PERF] Refresh already scheduled, updating args [${callId}]`);
            return;
        }

        // Clear any existing timer
        if (refreshDebounceTimer.current) {
            clearTimeout(refreshDebounceTimer.current);
        }

        // For urgent calls (force or showAll change), execute immediately
        if (forceRefresh || isShowAllChange) {
            console.log(`[GALLERY_PERF] Urgent call, executing immediately [${callId}] - FROM_APPEND_NEW_IMAGE: ${forceRefresh && !isShowAllChange}`);
            isRefreshScheduled.current = false;
            return refreshImagesActual(forceRefresh, isShowAllChange, callId);
        }

        // Regular debounced execution
        isRefreshScheduled.current = true;
        refreshDebounceTimer.current = setTimeout(() => {
            const args = pendingRefreshArgs.current;
            console.log(`[GALLERY_PERF] Debounce timer fired, executing [${args.callId}]`);
            isRefreshScheduled.current = false;
            refreshImagesActual(args.forceRefresh, args.isShowAllChange, args.callId);
        }, REFRESH_DEBOUNCE_MS);
    };

    // OPTIMIZED refreshImages function with performance improvements
    const refreshImagesActual = async (forceRefresh = false, isShowAllChange = false, callId = null) => {
        // PERFORMANCE: Start timing for optimization analysis
        const startTime = performance.now();
        const queryStartTime = Date.now();

        // PERFORMANCE: Create unique call identifier for debugging
        if (!callId) {
            callId = Math.random().toString(36).substr(2, 9);
        }
        console.log(`[GALLERY_PERF] refreshImagesActual executing [${callId}]: ${category}/${sortMethod}, page=${page}, showAll=${showAll}, images=${imageList.length}`);

        // ENHANCED CONCURRENCY CONTROL: Check for stuck queries and force cleanup
        if (queryBusy) {
            const stuckDuration = activeCallId.current ? Date.now() - queryStartTime : 0;
            if (stuckDuration > QUERY_TIMEOUT_MS) {
                console.warn(`[GALLERY_PERF] Previous query stuck for ${stuckDuration}ms, forcing cleanup`);
                setQueryBusy(false);
                activeCallId.current = null;
                if (queryTimeout.current) {
                    clearTimeout(queryTimeout.current);
                    queryTimeout.current = null;
                }
            } else if (!forceRefresh && !isShowAllChange) {
                console.log(`[GALLERY_PERF] Query busy [${activeCallId.current}], REJECTING concurrent call [${callId}]`);
                return;
            } else {
                console.log(`[GALLERY_PERF] Forcing through urgent call [${callId}], cancelling previous [${activeCallId.current}]`);
            }
        }

        // SET BUSY IMMEDIATELY with timeout protection
        setQueryBusy(true);
        activeCallId.current = callId;

        // Set timeout to prevent stuck queries
        // queryTimeout.current = setTimeout(() => {
        //     console.error(`[GALLERY_PERF] Query timeout for call [${callId}] after ${QUERY_TIMEOUT_MS}ms`);
        //     if (activeCallId.current === callId) {
        //         setQueryBusy(false);
        //         activeCallId.current = null;
        //     }
        // }, QUERY_TIMEOUT_MS);

        electronBridge.log("React - Refresh Images Start (OPTIMIZED)")

        // Sequence tracking for concurrent request handling
        const mySeq = ++refreshSeqRef.current;
        console.log(`[GALLERY_PERF] Call [${callId}] assigned sequence ${mySeq}, proceeding with query`);

        try {
            // PERFORMANCE: Check database version and available indexes
            const dbVersion = db.verno;
            const hasNewIndexes = dbVersion >= 26;
            console.log(`[GALLERY_PERF] Database version: ${dbVersion}, New indexes available: ${hasNewIndexes}`);

            // PERFORMANCE: If we have indexes but still slow, there's a bug in our logic
            if (hasNewIndexes) {
                console.log(`[GALLERY_PERF] Using INDEXED queries - should be fast for ${category}/${sortMethod}`);
            } else {
                console.log(`[GALLERY_PERF] Using FALLBACK queries - will be slower`);
            }

            let queryDesc = "base";
            let useMemorySort = false;
            let memoryFilterFn = null;

            // Build the base query based on category and sort method
            let query;

            // OPTIMIZED QUERIES: Use the most efficient query patterns for each case
            if (category === "All") {
                if (sortMethod === "Newest") {
                    if (hasNewIndexes) {
                        // Use compound index: [receivedtimestamp] (descending)
                        query = db.images.orderBy('receivedtimestamp').reverse();
                        queryDesc = "All/Newest(receivedtimestamp-index)";
                    } else {
                        query = db.images.reverse();
                        queryDesc = "All/Newest(reverse)";
                    }
                } else if (sortMethod === "Oldest") {
                    if (hasNewIndexes) {
                        // Use compound index: [receivedtimestamp] (ascending)
                        query = db.images.orderBy('receivedtimestamp');
                        queryDesc = "All/Oldest(receivedtimestamp-index)";
                    } else {
                        query = db.images.toCollection();
                        queryDesc = "All/Oldest(collection)";
                    }
                } else if (sortMethod === "New_requested") {
                    if (hasNewIndexes) {
                        query = db.images.orderBy('requestedtimestamp').reverse();
                        queryDesc = "All/New_requested(requestedtimestamp-index)";
                    } else {
                        query = db.images.toCollection();
                        useMemorySort = true;
                        queryDesc = "All/New_requested(memory-sort)";
                    }
                } else if (sortMethod === "Old_requested") {
                    if (hasNewIndexes) {
                        query = db.images.orderBy('requestedtimestamp');
                        queryDesc = "All/Old_requested(requestedtimestamp-index)";
                    } else {
                        query = db.images.toCollection();
                        useMemorySort = true;
                        queryDesc = "All/Old_requested(memory-sort)";
                    }
                } else if (sortMethod === "Non-Tagged-Asc" || sortMethod === "Non-Tagged-Desc") {
                    query = db.images.where('tag').equals(undefined).or('tag').equals("");
                    if (sortMethod === "Non-Tagged-Asc") query = query.reverse();
                    queryDesc = `All/${sortMethod}(tag-filter)`;
                } else if (sortMethod === "AIHorde-Images-Asc" || sortMethod === "AIHorde-Images-Desc") {
                    query = db.images.where('model').startsWithIgnoreCase('AI-Horde');
                    if (sortMethod === "AIHorde-Images-Asc") query = query.reverse();
                    queryDesc = `All/${sortMethod}(model-filter)`;
                } else {
                    // Random or other sorts always need memory sort
                    query = db.images.toCollection();
                    useMemorySort = true;
                    queryDesc = `All/${sortMethod}(memory-sort)`;
                }
            } else {
                // CATEGORY FILTERING: Use tag indexes when available
                if (hasNewIndexes && (category === "Public" || category === "Private")) {
                    // Use public field index if available
                    const isPublic = category === "Public";
                    if (sortMethod === "Newest") {
                        query = db.images.where('public').equals(isPublic).reverse();
                        queryDesc = `${category}/Newest(public-index)`;
                    } else if (sortMethod === "Oldest") {
                        query = db.images.where('public').equals(isPublic);
                        queryDesc = `${category}/Oldest(public-index)`;
                    } else {
                        query = db.images.where('public').equals(isPublic);
                        useMemorySort = true;
                        queryDesc = `${category}/${sortMethod}(public-index+memory-sort)`;
                    }
                } else {
                    // Use filtering for custom categories
                    query = db.images.toCollection();

                    if (category === "Non-Tagged") {
                        memoryFilterFn = (image) => !image.tag || image.tag === "";
                        queryDesc += ` + non-tagged(filter)`;
                    } else if (category === "Failed") {
                        memoryFilterFn = (image) => image.image === "";
                        queryDesc += ` + failed(filter)`;
                    } else if (category === "Videos") {
                        memoryFilterFn = (image) => image.dataType === "video";
                        queryDesc += ` + videos(filter)`;
                    } else if (category === "Images") {
                        memoryFilterFn = (image) => image.dataType === "image";
                        queryDesc += ` + images(filter)`;
                    } else if (category === "Audio") {
                        memoryFilterFn = (image) => image.dataType === "audio";
                        queryDesc += ` + audio(filter)`;
                    } else if (category === "Public") {
                        memoryFilterFn = (image) => image.public === true;
                        queryDesc += ` + public(filter)`;
                    } else if (category === "Private") {
                        memoryFilterFn = (image) => image.public === false;
                        queryDesc += ` + private(filter)`;
                    } else {
                        // Custom tag filtering
                        memoryFilterFn = (image) => {
                            const tags = image.tag || "";
                            return tags.toLowerCase().includes(category.toLowerCase());
                        };
                        queryDesc += ` + tag:${category}(filter)`;
                    }
                }
            }

            console.log(`[GALLERY_PERF] Query strategy: ${queryDesc}`);

            // PERFORMANCE TEST: Quick baseline for most common case
            if (hasNewIndexes && category === "All" && sortMethod === "Newest" && !showAll) {
                console.log(`[GALLERY_PERF] FAST PATH: Using optimized All/Newest/Paginated query`);
            }

            // DIAGNOSTIC: Check if indexed query is actually slow, use fast fallback if needed
            let useFastFallback = false;
            if (hasNewIndexes && category === "All" && (sortMethod === "Newest" || sortMethod === "Oldest")) {
                // Test if we should use emergency fast mode
                const totalImages = await db.images.count();
                console.log(`[GALLERY_PERF] Database has ${totalImages} total images`);

                if (totalImages < 500) {
                    // For small datasets, use simple query if indexed query is problematic
                    console.log(`[GALLERY_PERF] Small dataset (${totalImages} images) - using fallback for speed`);
                    useFastFallback = true;
                }
            }

            // FIXED EXECUTION: Handle both Table and Collection objects with fallback option
            const queryExecStart = performance.now();
            let results;

            if (!showAll) {
                // Paginated execution with fast fallback option
                try {
                    if (useFastFallback) {
                        // EMERGENCY FAST MODE: Use simple reverse() for small datasets
                        console.log(`[GALLERY_PERF] Using EMERGENCY FAST MODE - simple reverse query`);
                        const fastQuery = db.images.reverse().offset(page * 20).limit(20);
                        results = await fastQuery.toArray();
                        queryDesc += " + EMERGENCY-FAST-MODE";
                    } else {
                        // Apply memory filtering if needed
                        if (memoryFilterFn) {
                            const allResults = await query.toArray();
                            const filteredResults = allResults.filter(memoryFilterFn);

                            // Apply memory sorting if needed
                            if (useMemorySort) {
                                if (sortMethod === "New_requested") {
                                    filteredResults.sort((a, b) => (b.requestedtimestamp || 0) - (a.requestedtimestamp || 0));
                                } else if (sortMethod === "Old_requested") {
                                    filteredResults.sort((a, b) => (a.requestedtimestamp || 0) - (b.requestedtimestamp || 0));
                                }
                            }

                            results = filteredResults.slice(page * 20, (page + 1) * 20);
                            queryDesc += " + memory-filter+paginated";
                        } else {
                            // Try pagination first (works with Table objects)
                            if (typeof query.offset === 'function') {
                                // DETAILED TIMING: Break down the slow query
                                const offsetStart = performance.now();
                                const offsetQuery = query.offset(page * 20);
                                const offsetTime = performance.now() - offsetStart;

                                const limitStart = performance.now();
                                const limitQuery = offsetQuery.limit(20);
                                const limitTime = performance.now() - limitStart;

                                const arrayStart = performance.now();
                                results = await limitQuery.toArray();
                                const arrayTime = performance.now() - arrayStart;

                                console.log(`[GALLERY_PERF] Query breakdown: offset(${offsetTime.toFixed(1)}ms) + limit(${limitTime.toFixed(1)}ms) + toArray(${arrayTime.toFixed(1)}ms)`);
                                queryDesc += " + paginated(db)";
                            } else {
                                // Fallback for Collection objects - get all and slice in memory
                                const allResults = await query.toArray();
                                results = allResults.slice(page * 20, (page + 1) * 20);
                                queryDesc += " + paginated(memory)";
                            }
                        }
                    }
                } catch (error) {
                    console.warn('[GALLERY_PERF] Pagination failed, using memory fallback:', error.message);
                    const allResults = await query.toArray();
                    results = allResults.slice(page * 20, (page + 1) * 20);
                    queryDesc += " + paginated(memory-fallback)";
                }
            } else {
                // Show All execution
                if (useFastFallback) {
                    console.log(`[GALLERY_PERF] Show All using EMERGENCY FAST MODE`);
                    results = await db.images.reverse().toArray();
                    queryDesc += " + show-all-EMERGENCY-FAST";
                } else {
                    let allResults = await query.toArray();

                    // Apply memory filtering if needed
                    if (memoryFilterFn) {
                        allResults = allResults.filter(memoryFilterFn);
                        queryDesc += " + memory-filter";
                    }

                    // Apply memory sorting if needed
                    if (useMemorySort) {
                        if (sortMethod === "New_requested") {
                            allResults.sort((a, b) => (b.requestedtimestamp || 0) - (a.requestedtimestamp || 0));
                        } else if (sortMethod === "Old_requested") {
                            allResults.sort((a, b) => (a.requestedtimestamp || 0) - (b.requestedtimestamp || 0));
                        }
                        queryDesc += " + memory-sort";
                    }

                    results = allResults;
                    queryDesc += " + show-all";
                }
            }

            const queryExecTime = performance.now() - queryExecStart;
            console.log(`[GALLERY_PERF] Call [${callId}] query completed in ${queryExecTime.toFixed(2)}ms, returned ${results.length} images. Final strategy: ${queryDesc}`);

            // Only update if this is still the latest refresh
            if (mySeq === refreshSeqRef.current) {
                console.log(`[GALLERY_PERF] ========== UPDATING IMAGE LIST ==========`);
                console.log(`[GALLERY_PERF] Call [${callId}] BEFORE setImageList:`, {
                    currentImageListLength: imageList.length,
                    newResultsLength: results.length,
                    totalStoredImages,
                    isGalleryInitializing,
                    firstResultId: results[0]?.id,
                    firstResultUid: results[0]?.uid
                });

                setImageList(results);
                console.log(`[GALLERY_PERF] Call [${callId}] updated UI with ${results.length} images`);

                // Set gallery as initialized after first successful load
                if (isGalleryInitializing) {
                    setIsGalleryInitializing(false);
                    console.log(`[GALLERY_PERF] Gallery initialization completed`);
                }

                console.log(`[GALLERY_PERF] ========== IMAGE LIST UPDATE COMPLETE ==========`);
            } else {
                console.log(`[GALLERY_PERF] Call [${callId}] DISCARDED (sequence ${mySeq} vs current ${refreshSeqRef.current})`);
            }

            // PERFORMANCE: Log total execution time
            const totalTime = performance.now() - startTime;
            console.log(`[GALLERY_PERF] Call [${callId}] total time: ${totalTime.toFixed(2)}ms`);

            if (totalTime > 1000) {
                console.warn(`[GALLERY_PERF] Call [${callId}] SLOW! Time: ${totalTime.toFixed(2)}ms, Strategy: ${queryDesc}`);
            }

        } catch (error) {
            console.error(`[GALLERY_PERF] Call [${callId}] ERROR:`, error);
            if (mySeq === refreshSeqRef.current) setImageList([]);
        } finally {
            // Clean up global lock and timeout for this specific call
            if (activeCallId.current === callId) {
                setQueryBusy(false);
                activeCallId.current = null;
                if (queryTimeout.current) {
                    clearTimeout(queryTimeout.current);
                    queryTimeout.current = null;
                }
            }
            console.log(`[GALLERY_PERF] Call [${callId}] finished, queryBusy state cleaned up`);
            electronBridge.log("React - Refresh Images End (OPTIMIZED)")
        }
    }

    const fetchTags = async () => {
        const tempSet = new Set();
        await db.images.each(record => {
            if (record.tag) {
              record.tag.split(",").forEach(tag => {
                if (tag) {
                  tempSet.add(tag.trim());
                }
              });
            } else {
              setEmpty(true);
            }
        });

        let uniqueArray = [];
        let lowercasedArray = [];
        
        Array.from(tempSet).forEach(value => {
            if (!lowercasedArray.includes(value.toLowerCase())) {
                uniqueArray.push(value);
                lowercasedArray.push(value.toLowerCase());
            }
        });

        lowercasedArray = null;
        
        setCategories(uniqueArray.sort((a, b) => a.localeCompare(b)));
        collectionContextDispatch({type: 'tagging', tags: uniqueArray});
    }

    useEffect(() =>{
        fetchTags();
    },[imageList])

    useEffect(() => {
        if (generateStatus.status === "loading") {
            // Workaround for batches not returning 100 progress when images are done.
            if (generateStatus.count > 0) {
                if (generateStatus.count === generateStatus.totalImages) {
                    console.log('Stopping with enough images', `${generateStatus.totalImages} of ${generateStatus.count}`);
                    generateStatusDispatch({type: 'done'});
                }
            }
        }
    }, [generateStatus.count, generateStatus.totalImages])

    // useEffect(() => {
    //     console.log('[STATUS_EFFECT] useEffect triggered by status change', {
    //         timestamp: Date.now(),
    //         newStatus: generateStatus.status,
    //         uid: generateStatus.uid,
    //         intervalId: generateStatus.intervalId,
    //         totalImages: generateStatus.totalImages,
    //         count: generateStatus.count,
    //         progress: generateStatus.progress
    //     });
        
    //     if (generateStatus.status === "generating") {
    //         // make sure the reducer is in the right mode and then start fetching the job
    //         if (generateStatus.uid.length > 0 && !generateStatus.intervalId) {
    //             // const intervalId = setInterval(async () => await fetchJob(), 2500);
    //             const fetchTimeout = setTimeout(fetchJob, 2500);
    //             generateStatusDispatch({type: 'loading', intervalId: fetchTimeout});
    //         }
    //     }
    //     if (generateStatus.status === "loading") {
    //         // Workaround for batches not returning 100 progress when images are done.
    //         if (generateStatus.count > 0) {
    //             if (generateStatus.count === generateStatus.totalImages && generateStatus.progress === 0) {
    //                 console.log('Stopping with enough images', `${generateStatus.totalImages} of ${generateStatus.count}`);
    //                 generateStatusDispatch({type: 'done'});
    //                 
    //                 
    //             }
    //         }
    //     }
    //     if (generateStatus.status === "done") {
    //         console.log('[STATUS_EFFECT] Status changed to done, calling getTotalStoredImages', {
    //             timestamp: Date.now(),
    //             previousImages: totalStoredImages,
    //             totalGenerated: generateStatus.totalImages
    //         });
    //         getTotalStoredImages();
    //     }
    // }, [generateStatus.status])

    useEffect(() => {
        if (Object.keys(generateStatus.jobs).length === 0) return;

        if (isFeatureEnabled('USE_NEW_JOB_MANAGER')) {
            console.log('[POLLING] Using new job manager for coordinated polling');
            // The job manager will handle polling coordination
            // We still need to listen for poll_job events to trigger fetchJob
            return; // Job manager handles polling
        } else {
            console.log('[POLLING] Using legacy polling system');
            const fetchLoop = setInterval(() => {
                fetchAllJobs(false, apiState); // Replace false if you're checking cosy mode dynamically
            }, 2500);

            return () => clearInterval(fetchLoop);
        }
    }, [generateStatus.jobs, apiState]);

    // New timeout monitoring for multi-job system
    useEffect(() => {
        // Only start timeout monitoring if we have jobs
        const hasAnyJobs = generateStatus.jobs && Object.keys(generateStatus.jobs).length > 0;
        if (!hasAnyJobs) return;

        const checkTimeouts = setInterval(() => {
            // Get fresh job data on each check to avoid stale closure
            const currentJobs = generateStatus.jobs || {};
            const activeJobs = Object.values(currentJobs).filter(job => job.status === 'generating');

            if (activeJobs.length === 0) return;

            const currentTime = Date.now();
            const TIMEOUT_DURATION = 90000; // 90 seconds

            console.log(`[TIMEOUT CHECK] Checking ${activeJobs.length} active jobs at ${new Date(currentTime).toLocaleTimeString()}`);

            activeJobs.forEach(job => {
                if (!job.uid) return;

                // Check if this job has timed out
                const lastTime = job.lastProgressTime || job.timestamp;
                const timeSinceLastProgress = currentTime - lastTime;

                console.log(`[TIMEOUT CHECK] Job ${job.uid}: Last progress ${Math.round(timeSinceLastProgress / 1000)}s ago (threshold: 90s)`);

                if (timeSinceLastProgress > TIMEOUT_DURATION) {
                    console.log(`[TIMEOUT] Job ${job.uid} has not made progress for ${Math.round(timeSinceLastProgress / 1000)} seconds. Timing out.`);

                    // Handle the timeout for this specific job
                    if (!failedImage) {
                        handleFailedImage(job.uid);
                    }

                    // Clear from pending jobs since it timed out
                    clearPendingJob(job.uid);

                    // Update job status to error
                    generateStatusDispatch({
                        type: 'updateJob',
                        uid: job.uid,
                        updates: {
                            status: 'error',
                            error: 'Job timed out - no progress for 90 seconds'
                        }
                    });

                    // Cancel the specific job
                    if (job.uid.includes("-")) {
                        cancelCosyJob(job.uid, apiState, generateStatusDispatch);
                    } else {
                        cancelJob(job.uid, account.accountNo, generateStatusDispatch);
                    }

                    // Remove job from tracking
                    setTimeout(() => {
                        generateStatusDispatch({ type: 'removeJob', uid: job.uid });
                        // Check if all jobs are complete and run cleanup
                        setTimeout(async () => {
                            const remainingJobs = Object.values(generateStatus.jobs || {})
                                .filter(j => j.status === 'generating' || j.status === 'pending');
                            if (remainingJobs.length === 0) {
                                await cleanupRemainingPlaceholders();
                            }
                        }, 1000);
                    }, 1000);
                }
            });
        }, 5000); // Check every 5 seconds

        return () => clearInterval(checkTimeouts);
    }, [generateStatus.jobs, failedImage, apiState, account.accountNo]);
    

    /**
     * Handle the cancel button
     * @returns {Promise<*>}
     */
    const doCancelJob = async () => {
        if (generateStatus.uid) {
            localStorage.setItem('isCancelling', true);
            generateStatusDispatch({type: 'cancel'});
            if (generateStatus.intervalId) {
                clearTimeout(generateStatus.intervalId);
            }
            
            
            if (generateStatus.uid.includes("-")) {
                return cancelCosyJob(generateStatus.uid, apiState, generateStatusDispatch).then(async() => {
                    generateStatusDispatch({type: 'ready'});
                    refreshImages(false);
                });
            } else {
                return cancelJob(generateStatus.uid, account.accountNo, generateStatusDispatch).then(async() => {
                    generateStatusDispatch({type: 'ready'});
                    refreshImages(false);
                    localStorage.setItem('remember_workload', JSON.stringify({
                        active: false,
                        uid: "",
                        count: 0,
                        totalImage: 0,
                        num: 0,
                    }))
                });
            }
        }
    }

    let num = 0;
    const handleFailedImage = async (uid) => {
        const job = generateStatus.jobs?.[uid];
        if (!job) {
            console.warn(`Job ${uid} not found when handling failed image`);
            return;
        }

        // Clear this job from pending jobs since it failed
        clearPendingJob(uid);
        
        // Query DB to get the most accurate number of images for this job
        // const actualImagesInDB = await db.images.where('uid').equals(uid).count();
        const actualImagesInDB = job.totalImages;
        const remainingImages = Math.max(0, (job.count || 0) - actualImagesInDB);
        console.log(`[FAILED IMAGE] Job ${uid} | Expected: ${job.count} | Existing: ${actualImagesInDB} | Placeholders: ${remainingImages}`);

        // Get all placeholders for this job at once
        const placeholders = await db.images
            .where('uid').equals(uid)
            .and(i => !i.image || i.image === "")
            .toArray();
        
        console.log(`[FAILED IMAGE] Found ${placeholders.length} placeholders to convert to failed images`);

        // Process each placeholder
        for (let i = 0; i < Math.min(placeholders.length, remainingImages); i++) {
            const ph = placeholders[i];
            if (!ph) {
                console.warn(`[FAILED IMAGE] No placeholder found at index ${i}`);
                continue;
            }

            let randomNum = Math.floor(Math.random() * 99999999);
            let hashed = md5(randomNum.toString());
            let image = '';
            await db.images.update(ph.id,{
                image,
                seed: '',
                uid: uid,
                model: '',
                inCollection: false,
                hash: hashed,
                online: apiState.online,
                tag: generateStatus.tag,
                requestedtimestamp: Date.now(),
                receivedtimestamp: Date.now(),
                dataType: ""
            })
            console.log(`[FAILED IMAGE] Converted placeholder ${ph.id} to failed image (${i + 1}/${Math.min(placeholders.length, remainingImages)})`);
        }

        cleanupRemainingPlaceholders(uid);  // Pass uid to only clean up this job's placeholders
        getTotalStoredImages();
    }

    const deletePlaceholders = async (uid) => {
        const job = generateStatus.jobs?.[uid];
        if (!job) {
            console.warn(`Job ${uid} not found when deleting placeholders`);
            return;
        }

        // Clear this job from pending jobs since it was cancelled
        clearPendingJob(uid);

        // Get all placeholders for this job
        const placeholders = await db.images
            .where('uid').equals(uid)
            .and(i => !i.image || i.image === "")
            .toArray();

        console.log(`[DELETE PLACEHOLDERS] Found ${placeholders.length} placeholders to delete for cancelled job ${uid}`);

        if (placeholders.length > 0) {
            // Get the IDs of placeholders to delete
            const placeholderIds = placeholders.map(ph => ph.id);

            // Delete all placeholders at once
            await db.images.bulkDelete(placeholderIds);

            console.log(`[DELETE PLACEHOLDERS] Successfully deleted ${placeholders.length} placeholders for cancelled job ${uid}`);

            // Refresh images to update the gallery
            getTotalStoredImages();
        } else {
            console.log(`[DELETE PLACEHOLDERS] No placeholders found to delete for job ${uid}`);
        }
    }

    async function checkIfHostsMatch(mainHost, host) {
        const records = await db.settings
            .where("key")
            .anyOf([mainHost, host])
            .toArray();
    
        if (records.length === 2) {
            return records[0].value === records[1].value;
        }
    
        return false; // One or both keys are missing
    }  
    
    let jobProgression = 0;

    // Fetch all active jobs concurrently
    const fetchAllJobs = async () => {
        try {
            // Get a fresh snapshot of state each time
            const state = JSON.parse(JSON.stringify(generateStatus)); 
            const jobIds = Object.keys(state.jobs || {});
            if (!jobIds.length) return;

            console.log("State: ", state);
            
            for (const jobId of jobIds) {
                const job = state.jobs[jobId];

                // Skip invalid or completed jobs
                if (!job || !job.uid) {
                    console.warn(`[fetchAllJobs] Skipping invalid job ${jobId}`);
                    continue;
                }

                // Handle cancelling jobs
                if (job.status === 'cancelling') {
                    console.log(`[fetchAllJobs] Job ${jobId} is cancelling, handling cleanup`);
                    await deletePlaceholders(jobId);
                    generateStatusDispatch({ type: 'removeJob', uid: jobId });
                    console.log(`[fetchAllJobs] Cancelled job ${jobId} removed from tracking`);
                    generateStatusDispatch({type: 'ready'});
                    continue;
                }

                if (job.status !== 'generating') {
                    console.log(`[fetchAllJobs] Job ${jobId} is ${job.status}, skipping.`);
                    continue;
                }

                if (job.count == job.totalImages) {
                    console.log(`[fetchAllJobs] Jobs completed workload but still in loop, removing now`);
                    generateStatusDispatch({ type: 'removeJob', jobId });
                    generateStatusDispatch({
                        type: 'updateJob',
                        jobId,
                        updates: { status: 'done' }
                    });
                    continue;
                }

                console.log(`[fetchAllJobs] Polling job ${jobId}...`);
                await fetchJob(jobId);
            }
        } catch (err) {
            console.error("[fetchAllJobs] Error polling jobs:", err);
        }
    };

    const fetchJob = async (jobUID=null) => {
        // Check for jobs being cancelled in multi-job system
        if (jobUID) {
            const currentJob = generateStatus.jobs?.[jobUID];
            if (currentJob && currentJob.status === 'cancelling') {
                console.log(`[CANCEL] Job ${jobUID} is being cancelled, deleting placeholders`);
                await deletePlaceholders(jobUID);

                // Remove job from multi-job tracking to prevent infinite loop
                generateStatusDispatch({ type: 'removeJob', uid: jobUID });
                console.log(`[CANCEL] Job ${jobUID} removed from tracking after cancellation`);

                generateStatusDispatch({type: 'ready'});
                return; // Stop processing this job
            }
        }

        if (localStorage.getItem('isCancelling') === 'true') {
            console.log("Job cancelled. Skipping fetch.");
            return;
        }

        console.log('[FETCH_JOB] fetchJob called', {
            timestamp: Date.now(),
            uid: generateStatus.uid,
            currentStatus: generateStatus.status,
            progress: generateStatus.progress,
            totalImages: generateStatus.totalImages,
            count: generateStatus.count
        });

        if (apiState.online == true) {
            
        }

        let uid = "";
        let GenerationError = false;
    
        if (!uid) {
            if (generateStatus.uid) {
                uid = generateStatus.uid;
            }

            if (jobUID !== null) {
                uid = jobUID
            }
        }

        generateStatusDispatch({
            type: 'updateJob',
            uid,
            updates: { status: 'generating' }
        });
    
        // Add null check for the setting
        let playSounds = false;
        try {
            const setting = await db.settings.where('key').equals('play_sounds').first();
            if (setting && setting.hasOwnProperty('value')) {
                playSounds = setting.value;
            } else {
                // Initialize the setting if it doesn't exist
                await db.settings.put({ key: 'play_sounds', value: false });
            }
        } catch (error) {
            console.error("Error checking play_sounds setting:", error);
        }

        let isCosyActive = false; 
        if (uid.includes("-")) {
            isCosyActive = true
        }        
        // checkIfHostsMatch(apiState.host,"local_cosy").then((matches) => {
        //     isCosyActive = matches
        // })

        console.log(`[FETCH JOB DEBUG] generateStatus: `, generateStatus);
        

        ////////////////////////////// DEBUG FOR TESTING OF FAILED IMAGES //////////////////////////////////
        // console.log('[FETCH JOB DEBUG] testing failed job');
        
        // // handleFailedImage(uid);

        // generateStatusDispatch({
        //     type: 'error',
        //     message: `An error occurred adding images. DEBUG`
        // });
        // // NEW MULTI JOB SYSTEM
        // generateStatusDispatch({
        //     type: 'updateJob',
        //     uid,
        //     updates: { status: 'error', error: `An error occurred adding images. DEBUG` }
        // });
        // // Remove job from multi-job tracking after a short delay (optional)
        // setTimeout(() => {
        //     generateStatusDispatch({ type: 'removeJob', uid });
        // }, 1000);
        // console.log("Error caught and cancelling job");
        // GenerationError = true;
        // 
        // 
        // // doCancelJob();
        // await cleanupRemainingPlaceholders()
        // return;
        ///////////////////////////////////////////////////////////////////////
    
        console.log("Fetching job data for: ", uid);
        if (uid) {
            // Get job info to retrieve server URL for progress polling
            const currentJob = generateStatus.jobs?.[uid];
            const jobServerUrl = currentJob?.serverUrl;
            const jobServerType = currentJob?.serverType;

            console.log(`[FETCH_JOB] Using server URL for job ${uid}:`, {
                serverUrl: jobServerUrl,
                serverType: jobServerType,
                fallbackToDefault: !jobServerUrl
            });

            getProgress(uid, isCosyActive, apiState, generateStatusDispatch, jobServerUrl)
                .then(async data => {
                    if (jobProgression !== data.progress) {
                        jobProgression = data.progress;
                        
                    }

                    console.log("[GET_PROGRESS] Fetching progress data: ", data);

                    let progress = data.progress 
                    if (data.percent) {
                        progress = data.percent
                    }

                    if (progress > 0 ) {
                        generateStatusDispatch({ type: 'progress', progress: progress });

                        // NEW: Multi-job progress with timeout tracking
                        const currentJob = generateStatus.jobs?.[uid];

                        // ✅ Defensive check: only update if job exists in context
                        if (currentJob) {
                            const hasProgressChanged = currentJob.lastProgress !== progress;

                            generateStatusDispatch({
                                type: 'updateJob',
                                uid,
                                updates: {
                                    progress: progress,
                                    lastProgressTime: hasProgressChanged ? Date.now() : (currentJob.lastProgressTime || Date.now()),
                                    lastProgress: progress
                                }
                            });
                        } else {
                            console.warn(`[fetchJob] Job ${uid} not found in GenerateContext, skipping multi-job progress update`);
                        }
                    }
                    // Helper function to extract error from status array
                    const getStatusError = (data) => {
                        if (data.status && Array.isArray(data.status)) {
                            // Check for Failed status first
                            const failedStatus = data.status.find(statusObj => statusObj.status === "Failed");
                            if (failedStatus && failedStatus.error) {
                                return { type: 'Failed', error: failedStatus.error };
                            }

                            // Check for Safety status
                            const safetyStatus = data.status.find(statusObj => statusObj.status === "Safety");
                            if (safetyStatus && safetyStatus.error) {
                                return { type: 'Safety', error: safetyStatus.error };
                            }
                        }
                        return null;
                    };

                    // ERROR HANDLING OF ANY SORT OF FAILED JOB FROM API RESPONSE
                    // Check for errors in status array first, then fall back to outer error field
                    const statusError = getStatusError(data);
                    const hasError = (Number(progress) === 100 &&
                        ((statusError !== null) ||
                         (data.error !== undefined && data.error !== "None")));

                    if (hasError) {
                        // Prioritize status error over outer error for better error details
                        const finalError = statusError ? statusError.error : data.error;
                        console.log("Error in data: ", finalError);
                        if (!failedImage) {
                            handleFailedImage(uid);
                        }
                        const errorMsg = typeof finalError === 'string' ? finalError : JSON.stringify(finalError);
                        if (errorMsg.includes("Insufficient Credits") || errorMsg.includes("Out of Credits")) {
                            Swal.fire({
                                title: "<strong>Out Of Credits? Go Unlimited</strong>",
                                html: `<p class="text-base mb-4">You can either top up for online use or download Promptus once to generate offline forever - no monthly fees.</p>
                                      <button id="topUpNowBtn" class="rounded-lg bg-yellow py-3 px-5 shadow-xl font-bold text-xl text-white uppercase">Top Up Now</button>
                                      <button id="goOfflineBtn" class="rounded-lg bg-red-600 py-3 px-5 shadow-xl font-bold text-xl text-white uppercase mt-3">Go Offline (One-Time Purchase)</button>`,
                                icon: "error",
                                showConfirmButton: false,
                                didOpen: () => {
                                    const btn = document.getElementById("topUpNowBtn");
                                    const nxtbtn = document.getElementById("goOfflineBtn");
                                    if (btn) btn.addEventListener("click", () => openPromptusCreditsStore('payments'));
                                    if (nxtbtn) nxtbtn.addEventListener("click", () => openPromptusCreditsStore('subscriptions'));
                                }
                            });
                            generateStatusDispatch({type: 'ready'});
                        } else if (statusError && statusError.type === 'Safety') {
                            Swal.fire({
                                title: 'Checked For Safety',
                                text: 'Some words in your prompt may trigger content filters. Edit your prompt or generate offline',
                                icon: "warning",
                                confirmButtonText: "Enhance Prompt",
                                confirmButtonColor: "#190A4E",
                                showDenyButton: true,
                                denyButtonText: 'Go Offline',
                                denyButtonColor: '#FFB02E'
                            }).then((result) => {
                                if (result.isConfirmed) {
                                    eventBus.emit('enhancePromptTrue');
                                }
                                if (result.isDenied) {
                                    eventBus.emit('offlineToggleTrue');
                                }
                            });
                            generateStatusDispatch({type: 'ready'});

                            // NEW: Multi-job tracking cleanup (same as regular error handling)
                            generateStatusDispatch({
                                type: 'updateJob',
                                uid,
                                updates: { status: 'error', error: `Safety: ${statusError.error}` }
                            });

                            // Remove job from multi-job tracking after a short delay
                            setTimeout(() => {
                                generateStatusDispatch({ type: 'removeJob', uid });
                                // Check if all jobs are complete and run cleanup
                                setTimeout(async () => {
                                    const remainingJobs = Object.values(generateStatus.jobs || {})
                                        .filter(job => job.status === 'generating' || job.status === 'pending');
                                    if (remainingJobs.length === 0) {
                                        await cleanupRemainingPlaceholders();
                                    }
                                }, 1000);
                            }, 1000);
                        } else {
                            generateStatusDispatch({ type: 'error', message: finalError });
                            // NEW: Multi-job progress
                            generateStatusDispatch({
                                type: 'updateJob',
                                uid,
                                updates: { status: 'error', error: finalError }
                            });
                            // Remove job from multi-job tracking after a short delay (optional)
                            setTimeout(() => {
                                generateStatusDispatch({ type: 'removeJob', uid });
                                // Check if all jobs are complete and run cleanup after error
                                setTimeout(async () => {
                                    const remainingJobs = Object.values(generateStatus.jobs || {})
                                        .filter(job => job.status === 'generating' || job.status === 'pending');
                                    if (remainingJobs.length === 0) {
                                        await cleanupRemainingPlaceholders();
                                    }
                                }, 1000);
                            }, 1000);
                        } 
                        GenerationError = true;
                        
                        
                        return;
                    }

                    ///////////////////////////////////// ONLINE PATH /////////////////////////////////////
                    if (data && data.output && Array.isArray(data.output)) {
                        console.log('[FETCH_JOB] Processing images sequentially', {
                            timestamp: Date.now(),
                            imageCount: data.output.length,
                            currentTotalImages: generateStatus.totalImages
                        });
                        
                        // Process images sequentially instead of in parallel
                        for (const item of data.output) {
                            try {
                                const outputUid = item.uid;
                                console.log("Image id from getProgress", outputUid);
        
                                if (outputUid) {
                                    try {
                                        // SINGLE STRENGTHENED DUPLICATE CHECK
                                        const currentJob = generateStatus.jobs?.[uid];

                                        // 1. Quick memory check first (fastest)
                                        if (currentJob?.processedUIDs?.includes(outputUid)) {
                                            console.log(`[DUPLICATE] Output UID ${outputUid} already processed for job ${uid}, skipping...`);
                                            continue;
                                        }

                                        // 2. Single comprehensive database check
                                        const existingRecord = await db.images.where('hash').equals(outputUid).first();
                                        if (existingRecord) {
                                            if (existingRecord.image && existingRecord.image !== "") {
                                                // Image already exists and has content - true duplicate
                                                console.log(`[DUPLICATE] Hash ${outputUid} already exists with image data, skipping...`);
                                                generateStatusDispatch({
                                                    type: 'addProcessedUID',
                                                    jobUid: uid,
                                                    outputUid: outputUid
                                                });
                                                generateStatusDispatch({
                                                    type: 'incrementJobImages',
                                                    uid,
                                                    setting: false
                                                });
                                                continue;
                                            } else {
                                                // Record exists but no image content - can be updated
                                                console.log(`[EXISTING_PLACEHOLDER] Found existing placeholder for ${outputUid}, will update...`);
                                            }
                                        }

                                        // 3. Proceed with image processing (either new record or updateable placeholder)
                                            let exifmeta = []
                                    
                                            const metadata = await db.collection.where('uid').equals(uid).first();
                                            if (metadata) {
                                                exifmeta.push(metadata);
                                            }

                                            // Retry logic for getGenerateOutput to handle cases where URL is not ready
                                            const getGenerateOutputWithRetry = async (uid, outputUid, maxRetries = 5, delayMs = 1000) => {
                                                for (let attempt = 1; attempt <= maxRetries; attempt++) {
                                                    try {
                                                        console.log(`[FETCH JOB] Attempting to get generate output (attempt ${attempt}/${maxRetries})`);
                                                        const result = await getGenerateOutput(uid, outputUid);

                                                        // Check if URL exists and is not empty
                                                        if (result.url && result.url.trim() !== '') {
                                                            console.log(`[FETCH JOB] URL found on attempt ${attempt}: ${result.url}`);
                                                            return result;
                                                        }

                                                        // If no URL but we have base64, and this is our last attempt, return it
                                                        if (attempt === maxRetries) {
                                                            console.log(`[FETCH JOB] Max retries reached, using base64 fallback`);
                                                            return result;
                                                        }

                                                        // Wait before next attempt
                                                        console.log(`[FETCH JOB] No URL found on attempt ${attempt}, waiting ${delayMs}ms before retry...`);
                                                        await new Promise(resolve => setTimeout(resolve, delayMs));

                                                    } catch (error) {
                                                        console.error(`[FETCH JOB] Error on attempt ${attempt}:`, error);
                                                        if (attempt === maxRetries) {
                                                            throw error;
                                                        }
                                                        await new Promise(resolve => setTimeout(resolve, delayMs));
                                                    }
                                                }
                                            };

                                            const image = await getGenerateOutputWithRetry(uid, outputUid);
                                            console.log("[FETCH JOB] Media Result: ", image);
                                            const dataType = await getMediaType(image.image)
                                            let savedFilePath = null
                                            if (electronBridge.isElectron) {
                                                if (typeof image.image == 'string') {
                                                    savedFilePath = await electronBridge.ipcRenderer.invoke("test_upload", image.image, localDirectory, JSON.stringify(exifmeta), dataType)
                                                }
                                            } 
                                            if (image) {
                                                // Unified placeholder finding logic
                                                let ph;
                                                let imageID;
                                                
                                                if (failedImage && generateStatus.failedWorkloadUID) {
                                                    // Failed workload regeneration
                                                    console.log("Failed workload regeneration: ", generateStatus.failedWorkloadUID);
                                                    const results = await db.images.where('uid').equals(generateStatus.failedWorkloadUID).and(item => item.image === '').toArray();
                                                    console.log(`RESULTS: ${results}`);
                                                    ph = results[0];
                                                    imageID = ph?.id;
                                                } else if (failedImage && failedImageID) {
                                                    // Failed image regeneration by ID
                                                    console.log("Failed image regeneration", failedImageID);
                                                    ph = await db.images.get(failedImageID);
                                                    imageID = failedImageID;
                                                } else if (existingRecord && (!existingRecord.image || existingRecord.image === "")) {
                                                    // Use the existing placeholder we found in duplicate check
                                                    console.log(`[FETCH JOB] Using existing placeholder for outputUID: ${outputUid}`);
                                                    ph = existingRecord;
                                                    imageID = existingRecord.id;
                                                } else {
                                                    // Normal image generation - find empty placeholder
                                                    const availablePlaceholders = await db.images
                                                        .where('uid').equals(uid)
                                                        .and(i => !i.image || i.image === "")
                                                        .toArray();
                                                    console.log(`[FETCH JOB] Available placeholders for job ${uid}:`, availablePlaceholders.length, '| Processing outputUID:', outputUid);

                                                    ph = availablePlaceholders[0]; // Get first available
                                                    imageID = ph?.id;
                                                }
                                                
                                                console.log('[FETCH JOB] Placeholder selection for outputUID:', outputUid, '| Found placeholder:', ph, '| ID:', imageID, '| Hash:', ph?.hash);
                                                
                                                // Validate URL and fallback to base64 if invalid
                                                let imageData;
                                                if (electronBridge.isElectron) {
                                                    imageData = savedFilePath;
                                                } else {
                                                    const isUrlValid = await validateMediaUrl(image.url);
                                                    if (isUrlValid) {
                                                        imageData = image.url;
                                                        console.log('[FETCH JOB] Using validated URL for image data');
                                                    } else {
                                                        imageData = image.image || image.url;
                                                        console.log('[FETCH JOB] URL validation failed, using base64 fallback or original URL');
                                                    }
                                                }

                                                // Unified processing for both normal and failed images
                                                if (ph || (!failedImage && !ph)) {
                                                    let promise;
                                                    if (ph) {
                                                        console.log('[FETCH JOB] Placeholder found. Using Dexie UPDATE operation ');
                                                        // Before updating, check if this placeholder already has a different hash
                                                        // and if that hash conflicts with our outputUid
                                                        if (ph.hash && ph.hash !== outputUid) {
                                                            // Check if outputUid is already used elsewhere
                                                            const conflictCheck = await db.images.where('hash').equals(outputUid).first();
                                                            if (conflictCheck && conflictCheck.id !== ph.id) {
                                                                console.error(`[DUPLICATE] Cannot update placeholder ${ph.id} with hash ${outputUid} - already used by record ${conflictCheck.id}`);
                                                                continue;
                                                            }
                                                        }

                                                        // Use database queue if enabled for image updates
                                                        const imageUpdateData = {
                                                                image: imageData,
                                                                seed: image.seed,
                                                                uid: uid,
                                                                model: image.model ?? '',
                                                                inCollection: false,
                                                                hash: outputUid,
                                                                online: apiState.online,
                                                                tag: generateStatus.tag,
                                                                prompt: image.prompt,
                                                                receivedtimestamp: Date.now(),
                                                                requestedtimestamp: Date.now(),
                                                                dataType: dataType.toLowerCase()
                                                            };

                                                        promise = isFeatureEnabled('USE_DATABASE_QUEUE')
                                                            ? databaseOperations.updateImage(imageID || ph.id, imageUpdateData)
                                                            : db.images.update(imageID || ph.id, imageUpdateData)
                                                        
                                                    } else {
                                                        console.log('[FETCH JOB] Placeholder not found. Using database add operation');
                                                        const newImageData = {
                                                            image: imageData,
                                                            seed: image.seed,
                                                            uid: uid,
                                                            model: image.model ?? '',
                                                            inCollection: false,
                                                            hash: outputUid,
                                                            online: apiState.online,
                                                            tag: generateStatus.tag,
                                                            prompt: image.prompt,
                                                            receivedtimestamp: Date.now(),
                                                            requestedtimestamp: Date.now(),
                                                            dataType: dataType.toLowerCase()
                                                        };

                                                        promise = isFeatureEnabled('USE_DATABASE_QUEUE')
                                                            ? databaseOperations.addImage(newImageData)
                                                            : db.images.put(newImageData)
                                                    }
                                                    promise    
                                                        .then(async (id) => {
                                                            setReceived(uid, outputUid);
                                                            num += 1;
                                                            
                                                            // Mark this output UID as processed
                                                            generateStatusDispatch({
                                                                type: 'addProcessedUID',
                                                                jobUid: uid,
                                                                outputUid: outputUid
                                                            });
                                                            
                                                            // if (electronBridge.isElectron) {
                                                            //     electronBridge.ipcRenderer.invoke("test_upload", image.image, localDirectory, JSON.stringify(exifmeta), dataType)
                                                            // }
                                                            // Use appendNewImage instead of refreshImages for better performance
                                                            console.log('[FETCH_JOB] Calling appendNewImage for received image', {
                                                                timestamp: Date.now(),
                                                                imageId: id,
                                                                imageHash: outputUid
                                                            });
                                                            await appendNewImage(imageID || ph.id || id, outputUid);
                                                            // Use atomic increment to avoid race conditions
                                                            generateStatusDispatch({
                                                                type: 'incrementJobImages',
                                                                uid,
                                                                setting: playSounds
                                                            });
                                                            // setUnfinished(tempArr);                                                            
                                                        })
                                                        .catch(e => {
                                                            // ConstraintError means the image already exists - this is not a real error
                                                            if (e.name === "ConstraintError") {
                                                                console.log(`[FETCH JOB] ConstraintError for hash ${outputUid} - image already exists, skipping silently`);
                                                                // Mark as processed since the image is already in the database
                                                                generateStatusDispatch({
                                                                    type: 'addProcessedUID',
                                                                    jobUid: uid,
                                                                    outputUid: outputUid
                                                                });
                                                                return; // Exit early - this is not a real error
                                                            }

                                                            // For all other errors, handle normally
                                                            clearPendingJob(uid);

                                                            if (!failedImage) {
                                                                handleFailedImage(uid);
                                                            }
                                                            console.error('Error while adding images:', e);
                                                            console.error('Error stack trace:', e.stack);
                                                            console.error("Inner Exception: ", e.inner);
                                                            console.error('Error message:', e.message);

                                                            console.error(e);
                                                            generateStatusDispatch({
                                                                type: 'error',
                                                                message: `An error occurred adding images. ${e.name}`
                                                            });
                                                            // NEW MULTI JOB SYSTEM
                                                            generateStatusDispatch({
                                                                type: 'updateJob',
                                                                uid,
                                                                updates: { status: 'error', error: `An error occurred adding images. ${e.name}` }
                                                            });
                                                            // Remove job from multi-job tracking after a short delay (optional)
                                                            setTimeout(() => {
                                                                generateStatusDispatch({ type: 'removeJob', uid });
                                                                // Check if all jobs are complete and run cleanup after error
                                                                setTimeout(async () => {
                                                                    const remainingJobs = Object.values(generateStatus.jobs || {})
                                                                        .filter(job => job.status === 'generating' || job.status === 'pending');
                                                                    if (remainingJobs.length === 0) {
                                                                        await cleanupRemainingPlaceholders();
                                                                    }
                                                                }, 1000);
                                                            }, 1000);
                                                            console.log("Error caught and cancelling job");
                                                            GenerationError = true;
                                                            
                                                            
                                                            // doCancelJob();
                                                        })
                                                        .finally(async () => {
                                                            console.log("Prompt in data: ", image.prompt);
                                                            // Reset failed image state if it was a failed regeneration
                                                            if (failedImage) {
                                                                imageID = null;
                                                                setFailedImage(false);
                                                            }
                                                            
                                                            // After processing images (before polling again)
                                                            const currentJob = generateStatus.jobs?.[uid];
                                                            const jobCount = currentJob?.count || 0;
                                                            const jobImages = (currentJob?.totalImages || 0) + (data.output?.length || 0);
                                                            console.log(`[FETCH JOB] : Checking if job is completed: ${JSON.stringify(currentJob)} | ${jobCount} | ${jobImages}]`);
                                                            
                                                            const isJobComplete = jobImages >= jobCount && // <-- enforce full image count
                                                                (Number(progress) >= 100 || data.complete === true);
                                                            if (isJobComplete) {
                                                                console.log("[FETCH JOB] Job completed");
                                                                // update the prompt
                                                                const items = await db.collection.where('uid').equals(generateStatus.uid).toArray();
                                                                if (items.length > 0) {
                                                                    let oldPrompt = items[0].prompt;
                                                                    oldPrompt.prompt.prompt = image.prompt;
                                                                    await db.collection.update(items[0].id, { prompt: oldPrompt });
                                                                }
                                                                setPage(0);
                                                                if (electronBridge.isElectron) {
                                                                    electronBridge.ipcRenderer.invoke("test_upload", image.image, localDirectory, JSON.stringify(exifmeta), dataType)
                                                                }
                                                                // Clear from pending jobs on completion
                                                                clearPendingJob(uid);
                                                                // Check if all jobs are complete and run cleanup BEFORE removing job
                                                                setTimeout(async () => {
                                                                    const remainingJobs = Object.values(generateStatus.jobs || {})
                                                                        .filter(job => job.status === 'generating' || job.status === 'pending');
                                                                    console.log(`[JOB COMPLETION] Checking for cleanup: ${remainingJobs.length} remaining active jobs`);
                                                                    if (remainingJobs.length === 0) {
                                                                        await cleanupRemainingPlaceholders();
                                                                    }

                                                                    // Remove job from tracking AFTER cleanup check
                                                                    generateStatusDispatch({ type: 'removeJob', uid });
                                                                }, 2000); // Give more time for cleanup
                                                                generateStatusDispatch({
                                                                    type: 'updateJob',
                                                                    uid,
                                                                    updates: { status: 'done' }
                                                                });
                                                                generateStatusDispatch({ type: 'done' });
                                                                
                                                                // Redundant cleanup call removed - handled above
                                                            }
                                                            getTokens(accountContext.accountDetails.accountNo).then(data => {
                                                                accountContextDispatch({type : "tokens", tokens: data.credits})
                                                            })
                                                            
                                                            
                                                        });
                                                }
                                            }
                                        
                                } catch (error) {
                                    console.error("Error in processing image: ", error);
                                }
                            }
                            } catch (error) {
                                console.error("Error in processing image:", error);
                                // Continue processing other images even if one fails
                                // Don't set GenerationError = true here to allow other images to process
                            }
                        }
                        // Sequential processing complete
                    } else if (apiState.online == false && data.prompt) {
                        console.log("Offline ==============================");
                        console.log("PROMPT DATA: ", data);
                        console.log("PROMPT DATA OUTPUT: ", data.prompt.output);
                        let generatedImages = [];
                        if (data.prompt.output) {
                            const { images, seeds, models } = data.prompt.output;
                            if (images) {
                                // await Promise.all(
                                //     images.map(async(image) =>{
                                //     })
                                // )
                                for (const image of images) {
                                    // }
                                    let md5_hash = md5(image);
                                    //     if (sameSeed) {
                                    //     md5_hash = md5(image + "PROMPTUS" + sameSeedDate.toString() + images.length.toString());
                                    // } else {
                                    //     md5_hash = md5(image);
                                    // }
                                    // Check if this output UID has already been processed for this job
                                    const currentJob = generateStatus.jobs?.[uid];
                                    if (currentJob?.processedUIDs?.includes(md5_hash)) {
                                        console.log(`[DUPLICATE] Output UID ${md5_hash} already processed for job ${uid}, skipping...`);
                                        continue;
                                    }
                                    
                                    // First check if this hash already exists
                                    const hashcheck = await db.images.where('hash').equals(md5_hash).first();
                                    if (hashcheck && hashcheck.image && hashcheck.image !== "") {
                                        console.log(`[DUPLICATE] Hash ${md5_hash} already exists with image, skipping...`);
                                        // Mark as processed anyway to prevent future attempts
                                        generateStatusDispatch({
                                            type: 'addProcessedUID',
                                            jobUid: uid,
                                            outputUid: md5_hash
                                        });
                                        continue;
                                    }
                                    const i = images.indexOf(image);
    
                                    let exifmeta = []
                                
                                    const metadata = await db.collection.where('uid').equals(uid).first();
                                    if (metadata) {
                                        exifmeta.push(metadata);
                                    }
    
                                    console.log("Length of the images: ", images.length);
                                    // const hashcheck = await db.images.where('hash').equals(md5_hash).first();

                                    const dataType = await getMediaType(image)
                                    let savedFilePath = null;
                                    if (electronBridge.isElectron) {
                                        if (typeof image == 'string') {
                                            savedFilePath = await electronBridge.ipcRenderer.invoke("test_upload", image, localDirectory, JSON.stringify(exifmeta), dataType)
                                        }
                                        console.log(`OFFLINE IMAGE PATH SAVE: ${savedFilePath}`);
                                    } 
                                    
                                    console.log(`[OFFLINE FETCH JOB] hash |${md5_hash}| compared to processed `, currentJob?.processedUIDs);
                                    
                                    if (!hashcheck && !currentJob?.processedUIDs?.includes(md5_hash)) {
                                        if (failedImage === false) {
                                        console.log(`[OFFLINE FETCH JOB] INSIDE `);
                                            // Find a placeholder to update (one without an image)
                                            const ph = await db.images
                                            .where('uid').equals(uid)
                                            .and(i => !i.image || i.image === "")
                                            .first();
                                        
                                        if (ph) {
                                            // Before updating, check if this placeholder already has a different hash
                                            // and if that hash conflicts with our outputUid
                                            if (ph.hash && ph.hash !== md5_hash) {
                                                // Check if md5_hash is already used elsewhere
                                                const conflictCheck = await db.images.where('hash').equals(md5_hash).first();
                                                if (conflictCheck && conflictCheck.id !== ph.id) {
                                                    console.error(`[DUPLICATE] Cannot update placeholder ${ph.id} with hash ${md5_hash} - already used by record ${conflictCheck.id}`);
                                                    continue;
                                                }
                                            }
                                            
                                            await db.images.update(ph.id, {
                                                    image: electronBridge.isElectron ? savedFilePath : image,
                                                    seed: seeds[i],
                                                    uid: uid,
                                                    model: models[i] ?? '',
                                                    inCollection: false,
                                                    hash: md5_hash,
                                                    online: apiState.online,
                                                    tag: generateStatus.tag,
                                                    prompt: data.prompt.prompt,
                                                    receivedtimestamp: Date.now(),
                                                    requestedtimestamp: Date.now(),
                                                    dataType: dataType.toLowerCase()
                                                })
                                                // await db.images.put({
                                                //     image: electronBridge.isElectron ? savedFilePath : image,
                                                //     seed: seeds[i],
                                                //     uid: uid,
                                                //     model: models[i] ?? '',
                                                //     inCollection: false,
                                                //     hash: md5_hash,
                                                //     online: apiState.online,
                                                //     tag: generateStatus.tag,
                                                //     prompt: data.prompt.prompt,
                                                //     receivedtimestamp: Date.now(),
                                                //     requestedtimestamp: Date.now(),
                                                //     dataType: dataType.toLowerCase()
                                                // })
                                                    .then(async (id) => {
                                                        num += 1;
                                                        // Mark this output UID as processed
                                                        generateStatusDispatch({
                                                            type: 'addProcessedUID',
                                                            jobUid: uid,
                                                            outputUid: md5_hash
                                                        });
                                                        // Use appendNewImage for offline generated images
                                                        await appendNewImage(ph.id, md5_hash);
                                                        // Use atomic increment to avoid race conditions
                                                        generateStatusDispatch({
                                                            type: 'incrementJobImages',
                                                            uid,
                                                            setting: playSounds
                                                        });
                                                    })
                                                    .catch(e => {
                                                        // Always clear pending job on error
                                                        clearPendingJob(uid);
                                                        
                                                        if (!failedImage) {
                                                            handleFailedImage(uid);
                                                        }
                                                        console.error('Error while updating placeholder:', e);
                                                        console.error('Error stack trace:', e.stack);
                                                        console.error("Inner Exception: ", e.inner);
                                                        console.error('Error message:', e.message);
    
                                                        if (e.name !== "ConstraintError") {
                                                            console.error(e);
                                                            generateStatusDispatch({ type: 'error', message: `An error occurred adding images. ${e.name}` });
                                                            // NEW MULTI JOB SYSTEM
                                                            generateStatusDispatch({
                                                                type: 'updateJob',
                                                                uid,
                                                                updates: { status: 'error', error: `An error occurred adding images. ${e.name}` }
                                                            });
                                                        }
                                                        if (e.name === "ConstraintError") {
                                                            console.error(e);
                                                            generateStatusDispatch({ type: 'error', message: `An error occurred adding images. ${e.name}` });
                                                            // NEW MULTI JOB SYSTEM
                                                            generateStatusDispatch({
                                                                type: 'updateJob',
                                                                uid,
                                                                updates: { status: 'error', error: `An error occurred adding images. ${e.name}` }
                                                            });
                                                        }
                                                        generateStatusDispatch({ type: 'error', message: `An error occurred adding images. ${e.name}` });
                                                        // NEW MULTI JOB SYSTEM
                                                        generateStatusDispatch({
                                                            type: 'updateJob',
                                                            uid,
                                                            updates: { status: 'error', error: `An error occurred adding images. ${e.name}` }
                                                        });
                                                        // Remove job from multi-job tracking after a short delay (optional)
                                                        setTimeout(() => {
                                                            generateStatusDispatch({ type: 'removeJob', uid });
                                                            // Check if all jobs are complete and run cleanup after error
                                                            setTimeout(async () => {
                                                                const remainingJobs = Object.values(generateStatus.jobs || {})
                                                                    .filter(job => job.status === 'generating' || job.status === 'pending');
                                                                if (remainingJobs.length === 0) {
                                                                    await cleanupRemainingPlaceholders();
                                                                }
                                                            }, 3000);
                                                        }, 1000);
                                                        console.log("Error caught and cancelling job");
                                                        // doCancelJob();
                                                        GenerationError = true;
                                                        
                                                        
                                                    })
                                                    .finally(() => {
                                                        console.log("------------Data: ", data, "Generate Status: ", generateStatus);
                                                        // After processing images (before polling again)
                                                        const currentJob = generateStatus.jobs?.[uid];
                                                        const jobCount = currentJob?.count || 0;
                                                        // For offline jobs, use data.prompt.output.images instead of data.output
                                                        const outputLength = data.prompt?.output?.images?.length || 0;
                                                        const jobImages = (currentJob?.totalImages || 0) + outputLength;
                                                        console.log(`[FETCH JOB OFFLINE] : Checking if job is completed: ${JSON.stringify(currentJob)} | ${jobCount} | ${jobImages}]`);
                                                        
                                                        const isJobComplete =
                                                            jobImages >= jobCount && // <-- enforce full image count
                                                            (Number(progress) >= 100 || data.complete === true);
                                                        if (isJobComplete) {
                                                            console.log("Inside------------Data: ", data, "Generate Status: ", generateStatus);
                                                            // update the prompt
                                                            db.collection.where('uid').equals(generateStatus.uid).toArray(items => {
                                                                if (items.length > 0) {
                                                                    let oldPrompt = items[0].prompt;
                                                                    oldPrompt.prompt.prompt = data.prompt.prompt;
                                                                    db.collection.update(items[0].id, { prompt: oldPrompt });
                                                                }
                                                            });
                                                            setPage(0);
                                                            if (electronBridge.isElectron) {
                                                                electronBridge.ipcRenderer.invoke("test_upload", image, localDirectory, JSON.stringify(exifmeta), dataType)
                                                            }
                                                            // Remove job from multi-job tracking after a short delay (optional)
                                                            setTimeout(() => {
                                                                generateStatusDispatch({ type: 'removeJob', uid });
                                                                // Check if all jobs are complete and run cleanup after error
                                                                setTimeout(async () => {
                                                                    const remainingJobs = Object.values(generateStatus.jobs || {})
                                                                        .filter(job => job.status === 'generating' || job.status === 'pending');
                                                                    if (remainingJobs.length === 0) {
                                                                        await cleanupRemainingPlaceholders();
                                                                    }
                                                                }, 1000);
                                                            }, 1000);
                                                            generateStatusDispatch({
                                                                type: 'updateJob',
                                                                uid,
                                                                updates: { status: 'done' }
                                                            });
                                                            generateStatusDispatch({ type: 'done' });
                                                            setSameSeedDate(0)
                                                            // Check if all jobs are complete and run cleanup
                                                            setTimeout(async () => {
                                                                const remainingJobs = Object.values(generateStatus.jobs || {})
                                                                    .filter(job => job.status === 'generating' || job.status === 'pending');
                                                                if (remainingJobs.length === 0) {
                                                                    await cleanupRemainingPlaceholders();
                                                                }
                                                            }, 3000);
                                                        }
                                                        
                                                        
                                                    });
                                        }        
        
                                        } else if (failedImage === true) {
                                            let results;
                                            let imageID;
                                            
                                            if (generateStatus.failedWorkloadUID !== null) {
                                                console.log("Failed workload regeneration: ", generateStatus.failedWorkloadUID);
                                                results = await db.images.where('uid').equals(generateStatus.failedWorkloadUID).and(item => item.image === '').toArray();
                                                imageID = results[0].id;
                                            } else {
                                                console.log("Failed image regeneration", failedImageID);
                                                imageID = failedImageID;
                                            }
                                            // Only update if we have a valid imageID
                                            if (imageID) {
                                                await db.images.update(imageID, { image: image, seed: seeds[i], model: models[i] ?? '', hash: md5_hash, prompt: data.prompt.prompt, receivedtimestamp: Date.now() })
                                                .then(() => {
                                                    num += 1;
                                                    console.log("Image updated");
                                                    // generateStatusDispatch({ type: 'imageProgress', setting: playSounds });
                                                    // Use atomic increment to avoid race conditions
                                                    generateStatusDispatch({
                                                        type: 'incrementJobImages',
                                                        uid,
                                                        setting: playSounds
                                                    });
                                                    // Use appendNewImage for updated offline image
                                                    appendNewImage(imageID, md5_hash);
                                                })
                                                .finally(() => {
                                                    console.log("Image finished updating");
                                                    imageID = null;
                                                    results = null;
                                                    // After processing images (before polling again)
                                                    const currentJob = generateStatus.jobs?.[uid];
                                                    const jobCount = currentJob?.count || 0;
                                                    const jobImages = (currentJob?.totalImages || 0) + (data.output?.length || 0);
                                                    console.log(`[FETCH JOB] : Checking if job is completed: ${JSON.stringify(currentJob)} | ${jobCount} | ${jobImages}]`);
                                                    
                                                    const isJobComplete =
                                                        jobImages >= jobCount && // <-- enforce full image count
                                                        (Number(progress) >= 100 || data.complete === true);
                                                    if (isJobComplete) {
                                                        // update the prompt
                                                        db.collection.where('uid').equals(generateStatus.uid).toArray(items => {
                                                            if (items.length > 0) {
                                                                let oldPrompt = items[0].prompt;
                                                                oldPrompt.prompt.prompt = data.prompt.prompt;
                                                                db.collection.update(items[0].id, { prompt: oldPrompt });
                                                            }
                                                        });
                                                        setPage(0);
                                                        if (electronBridge.isElectron) {
                                                            electronBridge.ipcRenderer.invoke("test_upload", image, localDirectory, JSON.stringify(exifmeta), dataType)
                                                        }
                                                        // Remove job from multi-job tracking after a short delay (optional)
                                                        setTimeout(() => {
                                                            generateStatusDispatch({ type: 'removeJob', uid });
                                                        }, 1000);
                                                        generateStatusDispatch({
                                                            type: 'updateJob',
                                                            uid,
                                                            updates: { status: 'done' }
                                                        });
                                                        setFailedImage(false);
                                                        generateStatusDispatch({ type: 'done' });
                                                    }
                                                    
                                                    
                                                });
                                            }
                                        }
                                    }
                                }
                            }
                        }
                        
                    } else if (isCosyActive) {
                        if (progress == 100 || data.complete == true) {
                            // Clear from pending jobs on Cosy completion
                            generateStatusDispatch({ type: 'progress', progress: 100 });

                            // NEW: Multi-job progress
                            generateStatusDispatch({
                                type: 'updateJob',
                                uid,
                                updates: { 
                                    progress: 100,
                                    lastProgressTime: Date.now(),
                                    lastProgress: 100
                                }
                            });

                            progress = 100;

                            clearPendingJob(uid);
                            
                            // Track if we've processed all images for this cosyflow job
                            let processedCount = 0;
                            const totalExpectedImages = data.outputs?.length || 0;
                            
                            data.outputs.map(async(item) => {
                                console.log(`Output_id:`,item);

                                // Get job server URL for correct routing (same pattern as getProgress)
                                const currentJob = generateStatus.jobs?.[uid];
                                const jobServerUrl = currentJob?.serverUrl;

                                console.log(`[FETCH_JOB] Using server URL for getComfyOutput ${uid}:`, {
                                    serverUrl: jobServerUrl,
                                    serverType: currentJob?.serverType,
                                    fallbackToDefault: !jobServerUrl
                                });

                                const image = await getComfyOutput(uid,item.view.filename,apiState,generateStatusDispatch,jobServerUrl)
                                const dataType = await getMediaType(image.image)
                                const md5_hash = md5(image.image)
                                console.log(image);
                                console.log(JSON.stringify(image));
                                if (image) {
                                    // Check if image already exists before inserting
                                    let exifmeta = []
                                    
                                    const metadata = await db.collection.where('uid').equals(uid).first();
                                    if (metadata) {
                                        exifmeta.push(metadata);
                                    }
                                    const hashcheck = await db.images.where('hash').equals(md5_hash).first();
                                    if (!hashcheck) {
                                        // Find a placeholder to update (one without an image)
                                        const ph = await db.images
                                            .where('uid').equals(uid)
                                            .and(i => !i.image || i.image === "")
                                            .first();
                                        
                                        if (ph) {
                                            // Before updating, check if this placeholder already has a different hash
                                            // and if that hash conflicts with our md5_hash
                                            if (ph.hash && ph.hash !== md5_hash) {
                                                // Check if md5_hash is already used elsewhere
                                                const conflictCheck = await db.images.where('hash').equals(md5_hash).first();
                                                if (conflictCheck && conflictCheck.id !== ph.id) {
                                                    console.error(`[DUPLICATE] Cannot update placeholder ${ph.id} with hash ${md5_hash} - already used by record ${conflictCheck.id}`);
                                                    return;
                                                }
                                            }
                                            
                                            await db.images.update(ph.id, {
                                                image: image.image,
                                                seed: image.seed,
                                                uid: uid,
                                                model: image.model ?? '',
                                                inCollection: false,
                                                hash: md5_hash,
                                                online: apiState.online,
                                                tag: generateStatus.tag,
                                                prompt: image.prompt,
                                                receivedtimestamp: Date.now(),
                                                dataType: dataType.toLowerCase()
                                            })
                                            .then(async (id) => {
                                                num += 1;
                                                
                                                // Mark this output UID as processed
                                                generateStatusDispatch({
                                                    type: 'addProcessedUID',
                                                    jobUid: uid,
                                                    outputUid: md5_hash
                                                });
                                                
                                                // if (electronBridge.isElectron) {
                                                //     electronBridge.ipcRenderer.invoke("test_upload", image.image, localDirectory, JSON.stringify(exifmeta), dataType)
                                                // }
                                                // Use appendNewImage instead of refreshImages for better performance
                                                console.log('[FETCH_JOB] Calling appendNewImage for received image', {
                                                    timestamp: Date.now(),
                                                    imageId: id,
                                                    imageHash: md5_hash
                                                });
                                                await appendNewImage(ph.id, md5_hash);
                                                // Use atomic increment to avoid race conditions
                                                generateStatusDispatch({
                                                    type: 'incrementJobImages',
                                                    uid,
                                                    setting: playSounds
                                                });
                                                // setUnfinished(tempArr);                                                            
                                            })
                                            .catch(e => {
                                                // Always clear pending job on error
                                                clearPendingJob(uid);
                                                
                                                if (!failedImage) {
                                                    handleFailedImage(uid);
                                                }
                                                console.error('Error while adding images:', e);
                                                console.error('Error stack trace:', e.stack);
                                                console.error("Inner Exception: ", e.inner);
                                                console.error('Error message:', e.message);

                                                if (e.name !== "ConstraintError") {
                                                    console.error(e);
                                                    generateStatusDispatch({
                                                        type: 'error',
                                                        message: `An error occurred adding images. ${e.name}`
                                                    });
                                                    // NEW MULTI JOB SYSTEM
                                                    generateStatusDispatch({
                                                        type: 'updateJob',
                                                        uid,
                                                        updates: { status: 'error', error: `An error occurred adding images. ${e.name}` }
                                                    });
                                                }
                                                if (e.name === "ConstraintError") {
                                                    console.error(e);
                                                    generateStatusDispatch({
                                                        type: 'error',
                                                        message: `An error occurred adding images. ${e.name}`
                                                    });
                                                    // NEW MULTI JOB SYSTEM
                                                    generateStatusDispatch({
                                                        type: 'updateJob',
                                                        uid,
                                                        updates: { status: 'error', error: `An error occurred adding images. ${e.name}` }
                                                    });
                                                }
                                                generateStatusDispatch({
                                                    type: 'error',
                                                    message: `An error occurred adding images. ${e.name}`
                                                });
                                                // NEW MULTI JOB SYSTEM
                                                generateStatusDispatch({
                                                    type: 'updateJob',
                                                    uid,
                                                    updates: { status: 'error', error: `An error occurred adding images. ${e.name}` }
                                                });
                                                // Remove job from multi-job tracking after a short delay (optional)
                                                setTimeout(() => {
                                                    generateStatusDispatch({ type: 'removeJob', uid });
                                                    // Check if all jobs are complete and run cleanup after error
                                                    setTimeout(async () => {
                                                        const remainingJobs = Object.values(generateStatus.jobs || {})
                                                            .filter(job => job.status === 'generating' || job.status === 'pending');
                                                        if (remainingJobs.length === 0) {
                                                            await cleanupRemainingPlaceholders();
                                                        }
                                                    }, 1000);
                                                }, 1000);
                                                console.log("Error caught and cancelling job");
                                                GenerationError = true;
                                                
                                                
                                                // doCancelJob();
                                            })
                                            .finally(async () => {
                                                console.log("Prompt in data: ", image.prompt);
                                                // After processing images (before polling again)
                                                const currentJob = generateStatus.jobs?.[uid];
                                                const jobCount = currentJob?.count || 0;
                                                const jobImages = (currentJob?.totalImages || 0) + (data.output?.length || 0);
                                                console.log(`[FETCH JOB] : Checking if job is completed: ${JSON.stringify(currentJob)} | ${jobCount} | ${jobImages}]`);
                                                
                                                const isJobComplete =
                                                    jobImages >= jobCount && // <-- enforce full image count
                                                    (Number(progress) >= 100 || data.complete === true);
                                                if (isJobComplete) {
                                                    console.log("[FETCH JOB] Job completed");
                                                    // update the prompt
                                                    const items = await db.collection.where('uid').equals(generateStatus.uid).toArray();
                                                    if (items.length > 0) {
                                                        let oldPrompt = items[0].prompt;
                                                        oldPrompt.prompt.prompt = image.prompt;
                                                        await db.collection.update(items[0].id, { prompt: oldPrompt });
                                                    }
                                                    setPage(0);
                                                    if (electronBridge.isElectron) {
                                                        electronBridge.ipcRenderer.invoke("test_upload", image.image, localDirectory, JSON.stringify(exifmeta), dataType)
                                                    }
                                                    // Clear from pending jobs on completion
                                                    clearPendingJob(uid);
                                                    // Remove job from multi-job tracking after a short delay (optional)
                                                    setTimeout(() => {
                                                        generateStatusDispatch({ type: 'removeJob', uid });
                                                        // Check if all jobs are complete and run cleanup after error
                                                        setTimeout(async () => {
                                                            const remainingJobs = Object.values(generateStatus.jobs || {})
                                                                .filter(job => job.status === 'generating' || job.status === 'pending');
                                                            if (remainingJobs.length === 0) {
                                                                await cleanupRemainingPlaceholders();
                                                            }
                                                        }, 1000);
                                                    }, 1000);
                                                    generateStatusDispatch({
                                                        type: 'updateJob',
                                                        uid,
                                                        updates: { status: 'done' }
                                                    });
                                                    generateStatusDispatch({ type: 'done' });
                                                    
                                                    // Check if all jobs are complete and run cleanup
                                                    setTimeout(async () => {
                                                        const remainingJobs = Object.values(generateStatus.jobs || {})
                                                            .filter(job => job.status === 'generating' || job.status === 'pending');
                                                        if (remainingJobs.length === 0) {
                                                            await cleanupRemainingPlaceholders();
                                                        }
                                                    }, 3000);
                                                }
                                                getTokens(accountContext.accountDetails.accountNo).then(data => {
                                                    accountContextDispatch({type : "tokens", tokens: data.credits})
                                                })
                                                
                                                
                                            });
                                            // .then(async () => {
                                            //     num+=1
                                            //     // Use appendNewImage for ComfyUI generated images
                                            //     await appendNewImage(ph.id, md5_hash);
                                            //     console.log("Cosy image updated in placeholder");
                                            //     // Use atomic increment to avoid race conditions
                                            //     generateStatusDispatch({
                                            //         type: 'incrementJobImages',
                                            //         uid,
                                            //         setting: playSounds
                                            //     });
                                            // })
                                            // .finally(() => {
                                            //     generateStatusDispatch({ type: 'done' });
                                            // });
                                        } else {
                                            console.warn(`[COSY] No placeholder found for job ${uid}, cannot update image`);
                                        }
                                    } else {
                                        console.log("Image already exists with hash:", md5_hash);
                                        generateStatusDispatch({ type: 'done' });
                                    }
                                }
                                
                                // Increment processed count
                                processedCount++;
                                
                                // Check if all images have been processed
                                if (processedCount === totalExpectedImages) {
                                    console.log(`[COSY COMPLETE] All ${totalExpectedImages} images processed for job ${uid}`);
                                    
                                    // Mark job as done
                                    generateStatusDispatch({
                                        type: 'updateJob',
                                        uid,
                                        updates: { status: 'done' }
                                    });
                                    
                                    // Set overall status to done
                                    generateStatusDispatch({ type: 'done' });
                                    
                                    // Stop timer
                                    
                                    
                                    
                                    // Remove job from tracking after a delay
                                    setTimeout(() => {
                                        generateStatusDispatch({ type: 'removeJob', uid });
                                        
                                        // Clean up placeholders if no more jobs
                                        setTimeout(async () => {
                                            const remainingJobs = Object.values(generateStatus.jobs || {})
                                                .filter(job => job.status === 'generating' || job.status === 'pending');
                                            if (remainingJobs.length === 0) {
                                                await cleanupRemainingPlaceholders();
                                            }
                                        }, 1000);
                                    }, 1000);
                                }
                            })
                        }
                    }
                }).catch(error => {
                if (!failedImage) {
                    handleFailedImage(uid);
                }
                console.error('Error occurred fetching the generation progress:', error);
                console.error('Error stack trace:', error.stack);
                console.error("Inner Exception: ", error.inner);
                console.error('Error message:', error.message);
                generateStatusDispatch({
                    type: 'error',
                    message: "An error occurred fetching the generation progress"
                });
                // NEW MULTI JOB SYSTEM
                generateStatusDispatch({
                    type: 'updateJob',
                    uid,
                    updates: { status: 'error', error: "An error occurred fetching the generation progress" }
                });
                // Remove job from multi-job tracking after a short delay (optional)
                setTimeout(() => {
                    generateStatusDispatch({ type: 'removeJob', uid });
                    // Check if all jobs are complete and run cleanup after error
                    setTimeout(async () => {
                        const remainingJobs = Object.values(generateStatus.jobs || {})
                            .filter(job => job.status === 'generating' || job.status === 'pending');
                        if (remainingJobs.length === 0) {
                            await cleanupRemainingPlaceholders();
                        }
                    }, 1000);
                }, 1000);
                GenerationError = true;
                
                
            })
            .finally(() => {
                console.log("Fetching job finally");
                // const job = generateStatus.jobs?.[uid];
                // const isCancelling = localStorage.getItem('isCancelling') === 'true';
                // console.log("[fetchJob] Before rescheduling Job: ", job);
            
                // // ✅ Kill polling if job doesn't exist
                // if (!job) {
                //     console.log(`[fetchJob] Job ${uid} no longer exists — stopping polling.`);
                //     return;
                // }

                // // 🔴 Ultimate kill switch
                // if (!job?.uid) {
                //     console.log(`Job ${uid} has no UID — stopping polling`);
                //     return; // Don't reschedule
                // }

                // if (!isCancelling && job && job.status === 'generating' && !GenerationError) {
                //     console.log(`Re-polling job ${uid} in 2.5s`);
                //     const fetchTimeout = setTimeout(() => fetchJob(uid), 2500);
                //     generateStatusDispatch({ type: 'loading', intervalId: fetchTimeout });
                // } else {
                //     console.log(`Stopping polling for job ${uid} (status: ${job?.status || 'unknown'})`);
                // }
                // if (localStorage.getItem('isCancelling') === 'false' && generateStatus.status === "generating" && generateStatus.count !== num && !GenerationError) {
                //     console.log(":==================:", generateStatus, "num: ", num);
                //     const fetchTimeout = setTimeout(fetchJob, 2500);
                //     generateStatusDispatch({type: 'loading', intervalId: fetchTimeout});
                // }
            });
        } else {
            console.log("Fetching job finally");
            // const job = generateStatus.jobs?.[uid];
            // const isCancelling = localStorage.getItem('isCancelling') === 'true';
            // console.log("[fetchJob] Before rescheduling Job: ", job);
            
            // // ✅ Kill polling if job doesn't exist
            // if (!job) {
            //     console.log(`[fetchJob] Job ${uid} no longer exists — stopping polling.`);
            //     return;
            // }

            // // 🔴 Ultimate kill switch
            // if (!job?.uid) {
            //     console.log(`Job ${uid} has no UID — stopping polling`);
            //     return; // Don't reschedule
            // }

            // if (!isCancelling && job && job.status === 'generating' && !GenerationError) {
            //     console.log(`Re-polling job ${uid} in 2.5s`);
            //     const fetchTimeout = setTimeout(() => fetchJob(uid), 2500);
            //     generateStatusDispatch({ type: 'loading', intervalId: fetchTimeout });
            // } else {
            //     console.log(`Stopping polling for job ${uid} (status: ${job?.status || 'unknown'})`);
            // }
            // if (localStorage.getItem('isCancelling') === 'false' && generateStatus.status === "generating" && generateStatus.count !== num && !GenerationError) {
            //     console.log(":==================:", generateStatus);
            //     const fetchTimeout = setTimeout(fetchJob, 2500);
            //     generateStatusDispatch({type: 'loading', intervalId: fetchTimeout});
            // }
        }

    };
    
    /**
     * Clears the generated image collection
     * @returns {Promise<void>}
     */
    const doClearGenerated = async () => {
        Swal.fire({
            color: "#190A4E",
            title: `Clear all generated images under the selected filter: ${category}, this cannot be undone?`,
            icon: "info",
            confirmButtonText: 'Confirm',
            confirmButtonColor: "#FFB02E",
            showDenyButton: true,
            denyButtonText: "Cancel",
            denyButtonColor: "#190A4E",
        }).then(async(result) => {
            if (result.isConfirmed) {
                try {
                    console.log("Category: ", category);
                    let keysToDelete;
                    if (category === "All") {
                        await db.transaction('rw', db.images, async () => {
                            await db.images.clear();
                            getTotalStoredImages();
                        });   
                    } else if (category === "Failed") {
                        keysToDelete = await db.images.filter(image => image.image == "").primaryKeys();
                    } else {
                        keysToDelete = await db.images.filter(image => !isUndefined(image.tag) && image.tag.toLowerCase().includes(category.toLowerCase())).primaryKeys();
                    }
                    console.log("Keys to delete: ", keysToDelete);
                    if (keysToDelete.length > 0) {
                        await db.images.bulkDelete(keysToDelete);
                        console.log(`${keysToDelete.length} records deleted.`);
                        setCategory("All");
                      } else {
                        console.log('No records found with the specified value.');
                      }
                } catch (error) {
                    console.error("Error clearing images: ", error);
                }
            }
        })
    }

    useEffect(() => {
        electronBridge.log("React - Generate General UseEffect Start")

        getTotalStoredImages();
        electronBridge.log("React - Generate General UseEffect End")
    }, []);

    useEffect(() => {
        const showAllChanged = prevShowAllRef.current !== showAll;
        
        console.log('[FILTER_EFFECT] Page/filter change detected', {
            timestamp: Date.now(),
            page,
            showAll,
            prevShowAll: prevShowAllRef.current,
            showAllChanged,
            category,
            sortMethod,
            currentStatus: generateStatus.status,
            isJobActive: generateStatus.status === "loading" || generateStatus.status === "processing"
        });
        
        // Update the ref for next comparison
        prevShowAllRef.current = showAll;
        
        const isJobActive = generateStatus.status === "loading" || generateStatus.status === "processing";
        
        // Always allow refresh when showAll changes (user clicked the button)
        // This ensures the Show All/Show Pages button works during active jobs
        if (showAllChanged) {
            console.log('[FILTER_EFFECT] ShowAll changed, forcing refresh regardless of job status');
            // Pass true for both forceRefresh and isShowAllChange to ensure it always works
            refreshImages(true, true);
        } else if (!isJobActive) {
            // For other changes (page, category, sortMethod), only refresh if no job is active
            console.log('[FILTER_EFFECT] No active job, forcing refresh');
            refreshImages(true, false);
        } else {
            // During active jobs, skip refresh for non-showAll changes
            // This prevents the gallery from resetting to 20 images during job updates
            console.log('[FILTER_EFFECT] Job is active and showAll unchanged, skipping refresh to preserve state');
        }
    }, [page, showAll, category, sortMethod]);

    const getNumColumns = () => {
        switch(columnClass) {
            case "cols-1":
                return 1;
            case "cols-2":
                return 2;
            case "cols-3":
                return 3;
            default:
            case "cols-4":
                return 4;
            case "cols-5":
                return 5;
            case "cols-6":
                return 6;
        }
    }

    const handleEscapeKey = (e) => {
        console.log("Key pressed: ", e);
        
        if (e.key === "Escape") {
            setZoomedImage(null)
        }
    }

    useEffect(() => {
        if (zoomedImage) {
            window.addEventListener('keydown', handleEscapeKey);
        }

        // Cleanup the event listener on component unmount
        return () => {
            window.removeEventListener('keydown', handleEscapeKey);
        };
    }, [zoomedImage]);

    const failedImageViewing = async (imageDetails) => {
        console.log("Failed image viewing", imageDetails);
        
        // Close any existing zoom modal before opening details panel
        if (zoomedImage) {
            setZoomedImage(null);
        }
        
        if (typeof imageDetails === 'string') {
            const hashfailedDetailed = await db.images.where('hash').equals(imageDetails).first();
            setFailedImageDetails(hashfailedDetailed);
        } else {
            // imageDetails is now the full object with ID
            setFailedImageDetails(imageDetails);
        }    
        setViewFailedImage(true);
    }
    
    const handleRedos = (hash, seed, uid) => {
        if (hash !== null && seed == null && uid == null) {
            console.log("Redo | hash: ", hash);
            
            setRedoPrompt(hash);
            setRedoSeed("");
            setRedoPromptUid("");
        } else if (hash == null && seed !== null && uid == null) {
            console.log("Redo | Seed: ", seed);
            
            setRedoPrompt("");
            setRedoSeed(seed);
            setRedoPromptUid("");
        } else if (hash == null && seed == null && uid !== null) {
            console.log("Redo | UID: ", uid);
            
            setRedoPrompt("");
            setRedoSeed("");
            setRedoPromptUid(uid);
        }  
    }

    const tester2 = async() => {
        eventBus.emit('selectModel', {
            modelName: '(geminiimages) Nano Banana 2.5 Flash',
            openSettings: true
        });
    }

    const openPromptusCreditsStore = (webPage) => {
        console.log(`Opening Promptus Credits Store Page: `, webPage);
        if (electronBridge.isElectron) {
            ipcRenderer.send('open-urlInExternal', `https://login.promptus.ai/auto-signin?v=${accountContext.accountDetails.verificationToken}&e=${accountContext.accountDetails.emailHash}&a=${accountContext.accountDetails.accountNo}&r=/${webPage}`)
        } else {
            window.open(`https://login.promptus.ai/auto-signin?v=${accountContext.accountDetails.verificationToken}&e=${accountContext.accountDetails.emailHash}&a=${accountContext.accountDetails.accountNo}&r=/${webPage}`, '_blank');
        }
    };

    const jobsArray = Object.values(generateStatus.jobs || {}).sort((a, b) => {
        // Sort by job creation order — assume UID encodes order or use timestamp if available
        return b.timestamp - a.timestamp; // newest first
    });

    // RESERVED TOP SLOTS: keep placeholders at the top with stable keys, and replace in-place when images arrive
    const jobSlots = [];
    const usedImageIds = new Set();
    jobsArray.forEach(job => {
        const slotCount = job.count || 0;
        // Pick images for this job and sort by (requestedtimestamp || receivedtimestamp) ASC
        // to ensure left-to-right fill without reordering previously filled slots
        const jobImages = imageList
            .filter(img => img.uid === job.uid)
            .slice()
            .sort((a, b) => {
                const ta = (a.requestedtimestamp ?? a.receivedtimestamp ?? 0);
                const tb = (b.requestedtimestamp ?? b.receivedtimestamp ?? 0);
                if (ta === tb) {
                    // stable fallback to id if timestamps are equal
                    return (a.id ?? 0) - (b.id ?? 0);
                }
                return ta - tb;
            });

        const m = jobImages.length;
        const startIndex = Math.max(0, slotCount - m);
        for (let i = 0; i < slotCount; i++) {
            let image = null;
            if (i >= startIndex) {
                const idx = i - startIndex;
                if (idx < jobImages.length) {
                    image = jobImages[idx];
                }
            }
            jobSlots.push({
                key: `slot-${job.uid}-${i}`,
                image
            });
            if (image && image.id != null) {
                usedImageIds.add(image.id);
            }
        }
    });

    const cols = getNumColumns();
    const breakpointCols = useMemo(() => ({
    default: cols,
    2560: Math.min(cols, 6),
    1920: Math.min(cols, 6),
    1536: Math.min(cols, 5),
    1280: Math.min(cols, 4),
    1024: Math.min(cols, 3),
    768:  Math.min(cols, 3),
    640:  Math.min(cols, 2),
    320:  1
    }), [cols]);

    return (
        <>
            <PromptHeader
                defaultPrompt={defaultPrompt}
                handleGenerate={handleGenerate}
                cancelPrompt={doCancelJob}
                uid={redoPromptUid}
                prompt={redoPrompt}
                seed={redoSeed}
                inputImage={inputImage}
                pageNumber={setPage}
                setCategory={setCategory}
                fetchingUndelivered={fetchingUndelivered}
                deepLinkModel={deepLinkModel}
                templateData={templateData}
            />
            
            {/* Workflow Switch Notification */}
            {workflowSwitchNotification && (
                <div className="mx-4 mt-4 p-4 rounded-lg bg-blue-500/20 border border-blue-500/50 text-white">
                    <div className="flex items-center justify-between">
                        <div className="flex-1">
                            <p className="font-semibold">{workflowSwitchNotification.message}</p>
                            <p className="text-sm opacity-80 mt-1">
                                Previous model: {workflowSwitchNotification.originalModel}
                            </p>
                        </div>
                        <div className="flex gap-2 ml-4">
                            {workflowSwitchNotification.canRevert && originalWorkflowBeforeSwitch && (
                                <button
                                    onClick={() => {
                                        // Revert to original workflow
                                        const originalModel = originalWorkflowBeforeSwitch.model || originalWorkflowBeforeSwitch.workflow;
                                        if (originalModel) {
                                            localStorage.setItem('auto_selected_model', originalModel);
                                            window.dispatchEvent(new CustomEvent('modelAutoSwitch', {
                                                detail: {
                                                    newModel: originalModel,
                                                    reason: 'user_revert'
                                                }
                                            }));
                                            setWorkflowSwitchNotification(null);
                                            setOriginalWorkflowBeforeSwitch(null);
                                        }
                                    }}
                                    className="px-3 py-1 bg-gray-600 hover:bg-gray-700 rounded text-sm transition-colors"
                                >
                                    Revert
                                </button>
                            )}
                            <button
                                onClick={() => {
                                    setWorkflowSwitchNotification(null);
                                    setOriginalWorkflowBeforeSwitch(null);
                                }}
                                className="px-3 py-1 bg-gray-600 hover:bg-gray-700 rounded text-sm transition-colors"
                            >
                                Dismiss
                            </button>
                        </div>
                    </div>
                </div>
            )}
            
            {(() => {
                console.log('[UI_RENDER] Gallery render decision:', {
                    isGalleryInitializing,
                    totalStoredImages,
                    generateStatusLoading: generateStatus.loading,
                    imageListLength: imageList.length,
                    accountLoggedIn: accountContext.loggedIn,
                    willShowLoading: isGalleryInitializing,
                    willShowNoImages: !isGalleryInitializing && totalStoredImages === 0 && !generateStatus.loading,
                    willShowGallery: !isGalleryInitializing && !(totalStoredImages === 0 && !generateStatus.loading)
                });
                return null;
            })()}

            {isGalleryInitializing ? (
                <div className={"flex flex-col items-center justify-center py-8"}>
                    <div className="w-32 h-32 mb-4">
                        {animations ? (
                            <lottie-player
                                autoplay
                                loop
                                mode="normal"
                                src={PromptSpin}
                                style={{width: '128px', height: '128px'}}
                            ></lottie-player>
                        ) : (
                            <img src={Logo} alt={"Promptus Logo"} className="w-full h-full object-contain" />
                        )}
                    </div>
                    <p className={"text-xl text-center text-white"}>Loading gallery...</p>
                    <p className={"text-sm text-center text-white/80 mt-2"}>Fetching your images from the database</p>
                </div>
            ) : totalStoredImages === 0 && !generateStatus.loading ? (
                <div className={"flex flex-col items-center justify-center py-12"}>
                    <div className="text-6xl mb-4">🎨</div>
                    {accountContext.loggedIn ? (
                        <>
                            <h3 className={"text-2xl text-center font-semibold text-white mb-2"}>No images yet!</h3>
                            <p className={"text-lg text-center text-gray-400 mb-4 text-white"}>Start creating by entering a prompt above</p>
                            <p className={"text-sm text-center text-gray-500 text-white"}>Your generated images will appear here</p>
                        </>
                    ) : (
                        <>
                            <h3 className={"text-2xl text-center font-semibold text-white mb-2"}>Welcome to Promptus AI!</h3>
                            <p className={"text-lg text-center text-gray-400 mb-4"}>Please log in to start generating images</p>
                        </>
                    )}
                </div>
            ) :
                <div className={"px-2 sm:px-3 md:px-4 relative mb-3 sm:mb-4 md:mb-5"}>
                    {/* SEO H1 tag - visually hidden but important for search engines */}
                    <h1 className="sr-only">Promptus AI - AI Image Generator</h1>
                    <h2 className={"text-xl px-2 font-bold flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-0"}>
                        {/* <span>Results</span>
                        <hr className={"mx-2 flex-grow border border-white/40 bg-white/40"} /> */}
                        <div className="flex flex-wrap gap-2 w-full sm:w-auto">
                            <button 
                                onClick={() => {
                                    if (electronBridge.isElectron) {
                                        ipcRenderer.send("open-urlInExternal", 'https://discord.com/invite/gTTKzXKNay'); 
                                    } else {
                                        window.open("https://discord.com/invite/gTTKzXKNay", "_blank");
                                    }
                                }}
                                className="py-2 px-4 border rounded bg-indigo-600 hover:bg-indigo-700 transition text-white"
                            >
                                <span className="hidden max-xl-inclusive:inline">Join Our Discord</span>
                                <span className="max-xl-inclusive:hidden"><SiDiscord size={24} />
                                </span>
                            </button>
                            {/* <button onClick={tester2}>tester2</button> */}
                            <select className={"rounded-lg text-dark-blue border-0 p-2"} value={sortMethod} onChange={(e) => setSortMethod(e.target.value)}>
                                <option value="Newest">From Newest</option>
                                <option value="Oldest">From Oldest</option>
                                <option value="New_requested">Latest Requested</option>
                                <option value="Old_requested">Oldest Requested</option>
                                {empty ? 
                                    <>
                                        <option value={"Non-Tagged-Asc"}>Non-Tagged - New</option>
                                        <option value={"Non-Tagged-Desc"}>Non-Tagged - Old</option>
                                    </>
                                :
                                    null    
                                }
                                <option value={"AIHorde-Images-Asc"}>AI-Horde - New</option>
                                <option value={"AIHorde-Images-Desc"}>AI-Horde - Old</option>
                            </select>
                            <select className={"rounded-lg text-dark-blue border-0 p-2"} slot='5' value={category} onChange={(e) => setCategory(e.target.value)}>
                                <option value={"All"}>All</option>
                                <option value={"Images"}>Images Only</option>
                                <option value={"Videos"}>Videos Only</option>
                                <option value={"Audio"}>Audio Only</option>
                                <option value={"Failed"}>Regen</option>
                                {categories.map((category, i) => <option value={`${category}`}>{category.charAt(0).toUpperCase() + category.slice(1).toLowerCase()}</option>)}
                            </select>
                            <button className={"py-2 px-4 border rounded bg-red-400 transition"} onClick={doClearGenerated}>
                                <span className="hidden sm:inline">Delete All</span>
                                <span className="sm:hidden">Delete</span>
                            </button>
                        </div>
                        <div className={"flex flex-row justify-between sm:justify-end items-center w-full sm:w-auto p-4"}>
                            {!showAll ?
                                <>
                                    {page > 0 ?
                                        <button className={"text-4xl p-2"} onClick={() => setPage(page - 1)}>
                                            <img src={DoubleLeftIcon} alt="Previous Page" className='h-10'/>
                                        </button> 
                                        : 
                                        <button className={"text-lg sm:text-xl md:text-2xl p-0.5 sm:p-1 text-gray-400"} >
                                            <img src={GreyDoubleLeftIcon} alt="Previous Page" className='h-10'/>
                                        </button>}
                                    <div className={"text-xl p-2"} >{page + 1}</div>
                                    {(page * 20 + 20) < totalStoredImages ?
                                        <button className={"text-4xl p-2"} onClick={() => setPage(page + 1)}>
                                            <img src={DoubleRightIcon} alt="Next Page" className='h-10'/>
                                        </button> 
                                        :  
                                        <button className={"text-lg sm:text-xl md:text-2xl p-0.5 sm:p-1 text-gray-400"} >
                                            <img src={GreyDoubleRightIcon} alt="Next Page" className='h-10'/>
                                        </button>}
                                </>
                                    : 
                                null}
                            <button className={"py-2 px-4 mx-1 border rounded hover:bg-dark-blue transition"} onClick={() => setShowAll(!showAll)}>
                                <span className="hidden sm:inline">Show {showAll ? "Pages" : "All"}</span>
                                <span className="sm:hidden">{showAll ? "Pages" : "All"}</span>
                            </button>
                            <span className={"font-light mr-5"}>Images: <span className={"font-bold"}>{totalStoredImages}</span></span>
                            <select className={"rounded-lg text-dark-blue border-0 p-2"}
                                    value={columnClass} onChange={(e) => setColumnClass(e.target.value)}>
                                <option value={"cols-2"}>2</option>
                                <option value={"cols-3"}>3</option>
                                <option value={"cols-4"}>4</option>
                                <option value={"cols-5"}>5</option>
                                <option value={"cols-6"}>6</option>
                            </select>
                        </div>
                    </h2>
                    {/* Top reserved slots row: left-to-right fill */}
                    {/* <div className="p-4">
                        <div
                            className="grid gap-4"
                            style={{ gridTemplateColumns: `repeat(${getNumColumns()}, minmax(0, 1fr))` }}
                        >
                            {jobSlots.map((slot) => (
                                slot.image ? (
                                    <InView key={slot.key} triggerOnce threshold={0.1}>
                                        {({ inView, ref }) => (
                                            <div ref={ref}>
                                                {inView ? (
                                                    slot.image.dataType == "video" ? 
                                                        <Video 
                                                            {...slot.image}
                                                            isMyCollection={false}
                                                            dbTable={db.images}
                                                            tagArray={collectionContext.tags}
                                                            refreshTags={fetchTags}
                                                            refreshImages={getTotalStoredImages}
                                                        />
                                                    : slot.image.dataType == "audio" ?
                                                        <Audio 
                                                            {...slot.image}
                                                            name={slot.image.prompt || "Audio File"}
                                                        />
                                                    :
                                                        <CollectionImage
                                                            {...slot.image}
                                                            refreshImages={getTotalStoredImages}
                                                            setZoomedImage={(image) => setZoomedImage(image)}
                                                            setUid={(uid) => handleRedos(null,null,uid)}
                                                            setPrompt={(hash) => handleRedos(hash,null,null)}
                                                            setSeed={(seed) => handleRedos(null,seed,null)}
                                                            setImage={(image) => setInputImage({image})}
                                                            tagArray={collectionContext.tags}
                                                            dbTable={db.images}
                                                            refreshTags={fetchTags}
                                                            isMyCollection={false}
                                                            regenerateImage={handleGenerate}
                                                            viewFailedImage={failedImageViewing}  
                                                        />
                                                ) : (
                                                    <Placeholder Count={1} />
                                                )}
                                            </div>
                                        )}
                                    </InView>
                                ) : (
                                    <div key={slot.key}>
                                        <Placeholder Count={1} />
                                    </div>
                                )
                            ))}
                        </div>
                    </div> */}
                    <Masonry
                        breakpointCols={breakpointCols}
                        className="masonry-grid p-4 [contain:content]"
                        columnClassName="masonry-grid_column"
                    >
                        {/* {generateStatus.loading && !generateStatus.failedImage ? 
                            Array(Math.max(0, (generateStatus.count || 0) - (generateStatus.totalImages || 0))).fill().map((_, index) => (
                                <Placeholder key={`placeholder-direct-${index}`} Count={1}></Placeholder>
                            ))
                        : null
                        } */}{/* The rest of the images not used by reserved job slots */}
                        {imageList.map((image, i) => (
                        <InView key={image.id} triggerOnce threshold={0.1}>
                            {({ inView, ref, entry }) => (
                                <div ref={ref}>
                                    {inView ? (
                                        image.dataType == "video" ?
                                            <Video
                                                {...image}
                                                isMyCollection={false}
                                                dbTable={db.images}
                                                tagArray={collectionContext.tags}
                                                refreshTags={fetchTags}
                                                refreshImages={getTotalStoredImages}
                                            />
                                        : image.dataType == "audio" ?
                                            <Audio
                                                {...image}
                                                name={image.prompt || "Audio File"}
                                                isMyCollection={false}
                                                dbTable={db.images}
                                                tagArray={collectionContext.tags}
                                                refreshTags={fetchTags}
                                                refreshImages={getTotalStoredImages}
                                            />
                                        : image.dataType == "placeholder" ?
                                            <Placeholder Count={1} />
                                        :
                                            <CollectionImage
                                                {...image}
                                                refreshImages={getTotalStoredImages}
                                                setZoomedImage={(image) => setZoomedImage(image)}
                                                setUid={(uid) => handleRedos(null,null,uid)}
                                                setPrompt={(hash) => handleRedos(hash,null,null)}
                                                setSeed={(seed) => handleRedos(null,seed,null)}
                                                setImage={(image) => setInputImage({image})}
                                                tagArray={collectionContext.tags}
                                                dbTable={db.images}
                                                refreshTags={fetchTags}
                                                isMyCollection={false}
                                                regenerateImage={handleGenerate}
                                                viewFailedImage={failedImageViewing}
                                                onOpen={() => openViewerAt(image.hash)}  // <— single click opens viewer
                                            />
                                    ) : (
                                        <Placeholder Count={1} />
                                    )}
                                </div>
                            )}
                        </InView>
                        ))}
                    </Masonry>
                    {zoomedImage ?
                        <div className={"fixed top-0 left-0 z-10 flex flex-col justify-center place-items-center h-screen w-screen bg-dark-blue/40 backdrop-blur"} onKeyDown={(e) => handleEscapeKey(e)}>
                            <div className={"w-full mx-auto max-w-screen-lg bg-gray-500/50 flex flex-row justify-end"} style={{maxWidth: '900px'}}>
                                <button className={"px-5 py-2 rounded bg-dark-blue text-white m-2 hover:text-yellow transition"} onClick={() => setZoomedImage(null)}>
                                    <img src={CloseIcon} alt='Close Image' className='h-7'/>
                                </button>
                            </div>
                            {/*<div className={"relative w-full max-w-screen-lg"}>*/}
                            {/*    <button className={"absolute top-0 right-0 px-5 py-2 bg-dark-blue/40 rounded-lg hover:bg-dark-blue transition"} type={"button"}>Close</button>*/}
                            {/*    <img className={"w-full rounded-lg"} src={`data:image/webp;base64,${ zoomedImage.image }`} alt={`Generated image: ${ zoomedImage.seed }`} />*/}
                            {/*</div>*/}
                            <DrawingInput 
                                // image={zoomedImage.image && zoomedImage.image.includes(":\\") ? toFileUrl(zoomedImage.image) : `data:image/png;base64,${zoomedImage.image}`}
                                image={zoomedImage.image
                                    ? electronBridge.isElectron && zoomedImage.image.includes(":\\")
                                    ? toFileUrl(zoomedImage.image) // Electron + file path
                                    : zoomedImage.image.startsWith("http")
                                    ? zoomedImage.image // Remote URL
                                    : `data:image/png;base64,${zoomedImage.image}` // Base64
                                    : null
                                } 
                                setImage={(image) => setInputImage({image})} 
                                closeEvent={() => setZoomedImage(null)} 
                            />
                        </div>
                        : null}
                </div>
            }
            <div className={`flex flex-col w-96 bg-dark-blue h-full fixed z-50 top-0 right-0 ${viewFailedImage ? 'block' :'hidden'}`}>
                <div className='h-16 border-b flex flex-row justify-between items-center'>
                    <p className='text-lg font-medium ml-3 text-yellow'>Image Details: </p>
                    <button className={"w-12 h-12 p-1 mr-3"} title="Close Image Details" onClick={() => setViewFailedImage(false)}>
                        <img src={CloseIcon} alt='Close Image' className='h-7'/>
                    </button>
                </div>
                {viewFailedImage ?
                    <div className='mt-20 px-3'>
                        {(() => {
                            // Helper function to format field names
                            const formatFieldName = (key) => {
                                // Skip internal/system fields
                                const skipFields = ['id', 'uid', 'hash', 'jobUid', 'image', 'inCollection', 
                                                  'online', 'tag', 'receivedtimestamp', 'requestedtimestamp', 
                                                  'dataType', 'params'];
                                if (skipFields.includes(key)) return null;
                                
                                // Special formatting for known fields
                                const specialNames = {
                                    'text_0': 'Primary Prompt',
                                    'text_1': 'Secondary Prompt',
                                    'prompt': 'Prompt',
                                    'negative_prompt': 'Negative Prompt',
                                    'cfg': 'CFG Scale',
                                    'sampler_name': 'Sampler',
                                    'seed': 'Seed',
                                    'seed_value': 'Seed',
                                    'seed_mode': 'Seed Mode',
                                    'batch_size': 'Batch Size',
                                    'num_inference_steps': 'Steps',
                                    'steps': 'Steps',
                                    'guidance_scale': 'Guidance',
                                    'guidance': 'Guidance',
                                    'model': 'Model',
                                    'cosyflow': 'Cosy Flow',
                                    'denoise': 'Denoise',
                                    'scheduler': 'Scheduler',
                                    'width': 'Width',
                                    'height': 'Height',
                                    'lora': 'LoRA',
                                    'lora_scale': 'LoRA Scale',
                                    'count': 'Count',
                                    'control_net': 'Control Net',
                                    'image_batch_size': 'Image Batch Size',
                                    'upscale': 'Upscale',
                                    'upscale_factor': 'Upscale Factor',
                                    'safety_checker': 'Safety Checker',
                                    'symmetric': 'Symmetric',
                                    'shuffle': 'Shuffle'
                                };
                                
                                return specialNames[key] || key.split(/[_-]/)
                                    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                                    .join(' ');
                            };
                            
                            // Collect all fields to display
                            const fields = [];
                            const processedKeys = new Set();
                            
                            // Special handling for width and height to combine them
                            let width = failedImageDetails.width || failedImageDetails.params?.width;
                            let height = failedImageDetails.height || failedImageDetails.params?.height;
                            
                            // First add priority fields if they exist
                            const priorityFields = ['prompt', 'text_0', 'negative_prompt', 'text_1', 'model', 'cosyflow', 
                                                  'seed', 'seed_value', 'steps', 'num_inference_steps', 
                                                  'guidance', 'guidance_scale', 'cfg'];
                            
                            priorityFields.forEach(key => {
                                let value = failedImageDetails[key] || failedImageDetails.params?.[key];
                                if (value !== null && value !== undefined && value !== '' && !processedKeys.has(key)) {
                                    const label = formatFieldName(key);
                                    if (label) {
                                        // Special handling for seed
                                        if ((key === 'seed' || key === 'seed_value') && !processedKeys.has('seed')) {
                                            fields.push({ label: 'Seed', value: String(value), key: 'seed' });
                                            processedKeys.add('seed');
                                            processedKeys.add('seed_value');
                                        // Special handling for steps
                                        } else if ((key === 'steps' || key === 'num_inference_steps') && !processedKeys.has('steps')) {
                                            fields.push({ label: 'Steps', value: String(value), key: 'steps' });
                                            processedKeys.add('steps');
                                            processedKeys.add('num_inference_steps');
                                        // Special handling for guidance
                                        } else if ((key === 'guidance' || key === 'guidance_scale' || key === 'cfg') && !processedKeys.has('guidance')) {
                                            fields.push({ label: 'Guidance', value: String(value), key: 'guidance' });
                                            processedKeys.add('guidance');
                                            processedKeys.add('guidance_scale');
                                            processedKeys.add('cfg');
                                        } else if (!processedKeys.has(key)) {
                                            fields.push({ label, value: String(value), key });
                                            processedKeys.add(key);
                                        }
                                    }
                                }
                            });
                            
                            // Add height x width as combined field if both exist
                            if (width && height) {
                                fields.push({ label: 'Dimensions', value: `${height} × ${width}`, key: 'dimensions' });
                                processedKeys.add('width');
                                processedKeys.add('height');
                            }
                            
                            // Then add remaining top-level fields
                            Object.entries(failedImageDetails).forEach(([key, value]) => {
                                if (!processedKeys.has(key) && value !== null && value !== undefined && value !== '' && key !== 'params') {
                                    const label = formatFieldName(key);
                                    if (label) {
                                        fields.push({ label, value: String(value), key });
                                        processedKeys.add(key);
                                    }
                                }
                            });
                            
                            // Finally add remaining params fields
                            if (failedImageDetails.params) {
                                Object.entries(failedImageDetails.params).forEach(([key, value]) => {
                                    if (!processedKeys.has(key) && value !== null && value !== undefined && value !== '') {
                                        const label = formatFieldName(key);
                                        if (label) {
                                            fields.push({ label, value: String(value), key });
                                            processedKeys.add(key);
                                        }
                                    }
                                });
                            }
                            
                            // Render the fields
                            return fields.map((field, index) => (
                                <p key={field.key + index} className='font-semibold text-lg text-yellow'>
                                    {field.label}: <span className='text-white'>{field.value}</span>
                                </p>
                            ));
                        })()}
                        
                        {/* ReGen Button */}
                        <div className='mt-6 mb-4'>
                            <button 
                                className='w-full bg-yellow text-dark-blue font-bold rounded-lg px-4 py-3 hover:bg-yellow-400 transition'
                                onClick={async() => {
                                    console.log('Failed Image Details: ', failedImageDetails);
                                    
                                    const promptDetails = await db.collection.where('uid').equals(failedImageDetails.uid).toArray();
                                    
                                    console.log('Prompt Details: ', promptDetails[0].prompt);
                                    
                                    // Only proceed if we have valid image details
                                    if (!promptDetails) {
                                        console.error('Cannot regenerate: No valid image ID found');
                                        return;
                                    }
                                    
                                    // Prepare the prompt data for regeneration
                                    // const regenPromptData = {
                                    //     prompt: {
                                    //         prompt: failedImageDetails.prompt || '',
                                    //         negative_prompt: failedImageDetails.negative_prompt || '',
                                    //         enhanced: failedImageDetails.enhanced || false,
                                    //         prompt_output: failedImageDetails.prompt_output || 'text-image',
                                    //         audio_prompt: failedImageDetails.audio_prompt || '',
                                    //         input_language: failedImageDetails.input_language || 'en',
                                    //         meme_prompt: failedImageDetails.meme_prompt || false,
                                    //         params: {
                                    //             seed_value: failedImageDetails.params?.seed_value || failedImageDetails.seed || null,
                                    //             num_inference_steps: failedImageDetails.params?.num_inference_steps || 12,
                                    //             guidance_scale: failedImageDetails.params?.guidance_scale || 7.5,
                                    //             scheduler: failedImageDetails.params?.scheduler || 'DPM-Solver',
                                    //             model: failedImageDetails.params?.model || failedImageDetails.model || 'stable-diffusion-v1-5',
                                    //             lora: failedImageDetails.params?.lora || 'None',
                                    //             lora_scale: failedImageDetails.params?.lora_scale || 0,
                                    //             width: failedImageDetails.params?.width || 512,
                                    //             height: failedImageDetails.params?.height || 512,
                                    //             count: 1, // Generate only 1 image for single regen
                                    //             symmetric: failedImageDetails.params?.symmetric || false,
                                    //             shuffle: failedImageDetails.params?.shuffle || false,
                                    //             safety_checker: failedImageDetails.params?.safety_checker !== undefined ? failedImageDetails.params.safety_checker : true,
                                    //             input_image: failedImageDetails.params?.input_image || null,
                                    //             upscale: failedImageDetails.params?.upscale || false,
                                    //             upscale_factor: failedImageDetails.params?.upscale_factor || 0,
                                    //             control_net: failedImageDetails.params?.control_net || '',
                                    //             mask_image: failedImageDetails.params?.mask_image || null,
                                    //             image_batch_size: failedImageDetails.params?.image_batch_size || 1
                                    //         }
                                    //     }
                                    // };
                                    const isFailed = !!( await db.images.where('id').equals(failedImageDetails.id).and(item => item.image === '').first());
                                    console.log('Is Failed: ', isFailed);
                                    // Call handleGenerate with the prepared data
                                    if (isFailed) {
                                        handleGenerate(promptDetails[0].prompt, true, failedImageDetails.id);
                                    } else {
                                        handleGenerate(promptDetails[0].prompt, false, null);
                                    }
                                    
                                    // Close the details panel
                                    // setViewFailedImage(false);
                                }}
                            >
                                ReGen
                            </button>
                        </div>
                    </div>
                :
                    null
                }
            </div>
            {viewerOpen && viewable.length > 0 && (
                    <FullImageViewer
                        image={viewable[viewerIndex].image}
                        tag={viewable[viewerIndex].tag}
                        seed={viewable[viewerIndex].seed}
                        id={viewable[viewerIndex].id}
                        hash={viewable[viewerIndex].hash}
                        isMyCollection={false}
                        closeImageViewer={closeViewer}
                        onPrev={prevImage}
                        onNext={nextImage}
                        currentIndex={viewerIndex}
                        total={viewable.length}
                        // (optional) prefetch neighbors:
                        prevImageSrc={viewable[(viewerIndex - 1 + viewable.length) % viewable.length]?.image}
                        nextImageSrc={viewable[(viewerIndex + 1) % viewable.length]?.image}
                    />
            )}
        </>
    );
}
